import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TaskCompletionDialog } from "@/components/TaskCompletionDialog";
import { RexInboxTodayCards } from "@/components/RexInboxTodayCards";
import { DealStatusControl } from "@/components/DealStatusControl";
import { Radio, Zap, Plus, Send, X, Check, ExternalLink, RefreshCw, Target, Mail, FileText, Loader2, Megaphone, Clock, ChevronDown, ChevronUp, ChevronRight, Briefcase, Palette, Building2, Settings2, Award, AlertTriangle, Edit3, ArrowRight, Play, Users, Globe, Calendar, Telescope, Gavel } from "lucide-react";
import ScrumManagement from "@/pages/ScrumManagement";
import EngagementPromptsSection from "@/components/EngagementPromptsSection";
import RevenueSnapshot from "@/components/RevenueSnapshot";
import { ttlCacheClear } from "@/lib/ttlCache";
import { useClientDrawer } from "@/components/ClientDrawerProvider";
import AssignWithBriefModal, { buildOccasionBrief, buildBriefFromPayload } from "@/components/AssignWithBriefModal";
import BoardViews from "@/components/scrum/BoardViews";
import { AtRiskRow } from "@/components/today/AtRiskRow";

const fmtDate = d => { if (!d) return ""; try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return d?.substring(0,10)||""; }};
const urgCol = d => d > 30 ? "text-red-600" : d > 14 ? "text-amber-600" : "text-green-600";
const motionCol = { delivery: "border-l-blue-500 bg-blue-50/30", relationship: "border-l-purple-500 bg-purple-50/30", risk: "border-l-red-500 bg-red-50/30", commercial: "border-l-emerald-500 bg-emerald-50/30" };

// REX-110 — compact count tile used in the Marketing/Content summary on Today.
const CountTile = ({ label, count, colour }) => (
  <div className={`rounded-lg border px-3 py-2 ${colour}`} data-testid={`count-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
    <p className="text-xl font-black mt-0.5">{count}</p>
  </div>
);


const Sec = ({ icon: Icon, title, count, open, toggle, color, actions, children, id }) => (
  <div className={`border rounded-xl overflow-hidden ${color==="purple"?"border-purple-200 bg-purple-50/20":color==="blue"?"border-blue-200 bg-blue-50/20":"border-slate-200 bg-white"}`} data-testid={`section-${id}`}>
    <div className="flex items-center justify-between py-3 px-5 cursor-pointer hover:bg-slate-50/50" onClick={toggle}>
      <div className="flex items-center gap-3"><Icon className="h-5 w-5 text-slate-500"/><h2 className="text-base font-bold text-slate-800">{title}</h2><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${count>0?"bg-blue-100 text-blue-700":"bg-slate-100 text-slate-400"}`}>{count||0}</span></div>
      <div className="flex items-center gap-2" onClick={e=>e.stopPropagation()}>{actions}<span onClick={toggle} className="cursor-pointer p-1">{open?<ChevronUp className="h-5 w-5 text-slate-400"/>:<ChevronDown className="h-5 w-5 text-slate-400"/>}</span></div>
    </div>
    {open&&<div className="px-5 pb-4 space-y-2 border-t border-slate-100 pt-3">{children}</div>}
  </div>
);
const Sub = ({ children, color="slate" }) => <p className={`text-xs font-bold uppercase tracking-wider pt-3 pb-1 text-${color}-500`}>{children}</p>;

// N3 Commit C — the ONE shared refresh mechanism (same scrum:task-completed event the app uses).
const announceTaskChange = (task_id) =>
  window.dispatchEvent(new CustomEvent("scrum:task-completed", { detail: { task_id } }));

// N4 Commit A — one place decides what a Playbook item can do about its deal(s), from the truth
// on the wire (open_opps + total_opps). Create opportunity appears ONLY when total_opps === 0.
function PlaybookDealControls({ item, prefix, navigate, onChooseDeal, onFindClient }) {
  const open = item.open_opps || [];
  const hasCount = typeof item.total_opps === "number";
  // Never treat a stale item (no count on the wire) as zero — that would wrongly offer Create.
  const total = hasCount ? item.total_opps : (open.length || (item.opportunity_id ? 1 : 0));
  const cid = item.client_id;
  const stop = (e) => e.stopPropagation();
  if (open.length === 1 || (open.length === 0 && item.opportunity_id)) {
    return <Button size="sm" variant="outline" className="h-7 shrink-0" data-testid={`${prefix}-open-deal`} onClick={(e)=>{stop(e);navigate(`/clients/${cid}?tab=opportunities`);}}>Open deal</Button>;
  }
  if (open.length > 1) {
    return <Button size="sm" variant="outline" className="h-7 shrink-0" data-testid={`${prefix}-choose-deal`} onClick={(e)=>{stop(e);onChooseDeal(item);}}>{open.length} open deals — choose</Button>;
  }
  if (total > 0) {
    return (
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="text-[10px] text-slate-400 italic" data-testid={`${prefix}-none-open`}>Deals exist — none open</span>
        <div className="flex gap-1">
          {cid && <Button size="sm" variant="outline" className="h-7" data-testid={`${prefix}-open`} onClick={(e)=>{stop(e);navigate(`/clients/${cid}`);}}>Open client</Button>}
          {cid && <Button size="sm" variant="outline" className="h-7" data-testid={`${prefix}-see-deals`} onClick={(e)=>{stop(e);navigate(`/clients/${cid}?tab=opportunities`);}}>See existing deals</Button>}
        </div>
      </div>
    );
  }
  // total === 0 — the only case where Create opportunity is offered.
  if (!cid) {
    return onFindClient
      ? <Button size="sm" variant="outline" className="shrink-0" data-testid={`${prefix}-match`} onClick={(e)=>{stop(e);onFindClient(item);}}>Find / create client</Button>
      : <span className="text-[10px] text-slate-400 italic shrink-0" data-testid={`${prefix}-norecord`}>No linked record</span>;
  }
  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      <span className="text-[10px] text-slate-400 italic" data-testid={`${prefix}-nodeal`}>No deal linked</span>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" className="h-7" data-testid={`${prefix}-open`} onClick={(e)=>{stop(e);navigate(`/clients/${cid}`);}}>Open client</Button>
        <Button size="sm" variant="outline" className="h-7" data-testid={`${prefix}-createopp`} onClick={(e)=>{stop(e);navigate(`/clients/${cid}?tab=opportunities&new=opp`);}}>Create opportunity</Button>
      </div>
    </div>
  );
}

// Derive an internal Rex destination for a commitment that has no client and no
// external source_url, so its headline still opens something actionable.
const COMMIT_INTERNAL_PATH_RE = /\/(amplify|clients|scrum|mission-control|content-studio|triage|event-radar)[^\s"')]*/i;
function commitmentInternalPath(a) {
  const m = (a.description || "").match(COMMIT_INTERNAL_PATH_RE);
  if (m) return m[0];
  switch ((a.source_kind || "").toLowerCase()) {
    case "content": case "content_card": case "campaign": return "/amplify";
    case "task": return "/scrum";
    default: return null;
  }
}

export default function MorningScrumOS({ user }) {
  const navigate = useNavigate();
  const { openClient } = useClientDrawer();
  const canEditDeals = user?.role !== "viewer";  // L1 — same editor rule as the deal pages
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("founder");
  const [o, setO] = useState({intel:true,clients:false,engagements:true,pipeline:false,mkt:false,ops:false,scrum:false,campaigns:false,founder:false,commits:true});
  const tog = k => setO(p=>({...p,[k]:!p[k]}));
  const [updating, setUpdating] = useState(false);
  const loaded = useRef(false);

  const [newsItems, setNewsItems] = useState({today:[],previous:[]});
  const [monitorCount, setMonitorCount] = useState(0);
  const [waitCounts, setWaitCounts] = useState({ waiting: 0, chase_today: 0 });
  const [emailHealth, setEmailHealth] = useState(null); // N6: Today email-health line
  const [emailCount, setEmailCount] = useState(0); // Block One item 1: "From your emails" count in day numbers
  const [showEmailActivity, setShowEmailActivity] = useState(false); // #3 — banner "View" opens the honest outbox
  const [emailActivity, setEmailActivity] = useState(null);
  const [showMonitor, setShowMonitor] = useState(false);
  const [topMoves, setTopMoves] = useState([]);
  const [atRisk, setAtRisk] = useState({items:[],counts:{},total_items:0});
  const [atRiskFilter, setAtRiskFilter] = useState(null); // {type|'all', label} — opens the filtered At Risk queue
  const [atRiskQueue, setAtRiskQueue] = useState({ items: [], total_items: 0, loading: false });
  const [paymentsDue, setPaymentsDue] = useState({overdue:[],upcoming:[],forecast_past:[],held_summary:[],counts:{},overdue_by_currency:{}});
  const [designerHeatmap, setDesignerHeatmap] = useState({designers:[],totals:{}});
  const [lensTile, setLensTile] = useState({by_lane:{}, breached:0});
  const [heatmapDetail, setHeatmapDetail] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeTone, setNudgeTone] = useState("friendly");
  const [nudgeExtra, setNudgeExtra] = useState("");
  const [nudgeSubject, setNudgeSubject] = useState("");
  const [nudgeBody, setNudgeBody] = useState("");
  const [nudgeDrafting, setNudgeDrafting] = useState(false);
  const [nudgeSending, setNudgeSending] = useState(false);
  // Chase-email composer (stalled "Awaiting Client Response" tasks)
  const [chaseOpen, setChaseOpen] = useState(false);
  const [chaseDrafting, setChaseDrafting] = useState(false);
  const [chaseSending, setChaseSending] = useState(false);
  const [chaseTask, setChaseTask] = useState(null);
  const [chase, setChase] = useState({ subject: "", body: "", to_email: "", to_name: "", client_name: "", senders: [], sender_id: "" });
  const [execBoard, setExecBoard] = useState({lanes:{COMMERCIAL:[],CLIENT_DELIVERY:[],ADMIN_FINANCE:[],INTERNAL:[]},counts:{},orphan_count:0});
  const [myBoard, setMyBoard] = useState({ views: {}, counts: {} });
  const [execLaneOpen, setExecLaneOpen] = useState({COMMERCIAL:true,CLIENT_DELIVERY:true,ADMIN_FINANCE:true,INTERNAL:false});
  const [showLinkTask, setShowLinkTask] = useState(null);
  const [linkForm, setLinkForm] = useState({category:"",client_id:"",client_name:""});
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({title:"",description:"",category:"",client_id:"",client_name:"",assigned_to_name:"",due_date:"",priority:"medium"});
  const [weeklyPlaybook, setWeeklyPlaybook] = useState(null);
  const [showPlaybook, setShowPlaybook] = useState(true);
  // N3 Commit B — find/create a client for an account alert that matched no client record.
  const [clientMatch, setClientMatch] = useState(null); // { entity } | null
  const [matchResults, setMatchResults] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  // N4 Commit A — "several open deals" chooser: { company_name, client_id, open_opps } | null
  const [chooseDeal, setChooseDeal] = useState(null);
  const [clientHealth, setClientHealth] = useState([]);
  const [showConvEntry, setShowConvEntry] = useState(null);
  // N3 Commit C — "Save as a scrum action" form state for the conversation-entry dialog.
  const [convSave, setConvSave] = useState({ assignee_name: "", client_name: "", client_id: "", saving: false });
  const [convEntryLoading, setConvEntryLoading] = useState(false);
  const [showEditNBA, setShowEditNBA] = useState(null);
  const [nbaText, setNbaText] = useState("");
  // REX-004B — Analyst picker dialog state. Populated when /analyst-tasks/route
  // returns ``needs_assignment=true`` (i.e. > 1 analyst configured and no
  // explicit assignee_email was passed). The selected analyst is then PATCHed
  // onto the task via /analyst-tasks/{id}/assign.
  const [assignPrompt, setAssignPrompt] = useState(null); // { task_id, title, analysts: [{email,name}] } | null
  const [assignChoice, setAssignChoice] = useState("");
  const [signals, setSignals] = useState([]);
  const [signalHistory, setSignalHistory] = useState(null); // { sig, loading, rows } — "Assigned to X" trail
  const [tenders, setTenders] = useState([]);
  const [clientMotion, setClientMotion] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [scrumTasks, setScrumTasks] = useState([]);
  const [contentItems, setContentItems] = useState([]);
  const [ops, setOps] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: "", date_start: "", country: "", city: "", url: "", description: "" });
  const [founderQ, setFounderQ] = useState([]);
  const [commits, setCommits] = useState(null);
  const [commitUsers, setCommitUsers] = useState([]); // G1: reassign picker
  const [commitUI, setCommitUI] = useState(null);     // {id, mode:'notdoing'|'reassign'|'disambig', value, toId, candidates}
  const [momentum, setMomentum] = useState(null);
  const [senders, setSenders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [showVerifyCode, setShowVerifyCode] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [showDraftPreview, setShowDraftPreview] = useState(null);
  const [campDecision, setCampDecision] = useState({ kind: null, reason: "", date: "" });

  const [showCommit, setShowCommit] = useState(false);
  const [cf, setCf] = useState({title:"",source_section:"general",due_time:"17:00",client_name:"",founder_escalation:false,sender_id:""});
  const [replyText, setReplyText] = useState("");
  const [replyRes, setReplyRes] = useState(null);
  const [showNote, setShowNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [showArticle, setShowArticle] = useState(null);
  const [rexInstruction, setRexInstruction] = useState("");
  const [showActionRoute, setShowActionRoute] = useState(null);
  const [pipelineAsk, setPipelineAsk] = useState(null); // { company, candidates, payload, headline }
  const [actionRouteChoice, setActionRouteChoice] = useState("");
  const [actionRouteNote, setActionRouteNote] = useState("");
  const [replenishment, setReplenishment] = useState({ items: [], loaded: false });
  const [occasions, setOccasions] = useState({ items: [], loaded: false });
  const [expandedOcc, setExpandedOcc] = useState({});
  // REX-BRIEF (May 2026) — occasion-to-assign modal state. Holds the
  // occasion object until the user confirms in <AssignWithBriefModal>.
  const [occasionToAssign, setOccasionToAssign] = useState(null);
  const [showAddOccasion, setShowAddOccasion] = useState(false);
  const [newOccasion, setNewOccasion] = useState({ date: "", title: "", type: "custom", country: "", notes: "", content_opportunity: true });
  // REX-BRIEF-EVERYWHERE (May 2026) — generic "route with brief" intent state.
  // Any flow that spawns work for a teammate sets this. The shared modal
  // at the bottom of the component renders it. Shape:
  //   { title, subtitle, suggestedBrief, onConfirm, onCancel, confirmLabel? }
  const [routeIntent, setRouteIntent] = useState(null);
  // Pool of users for the modal picker — fetched once.
  const [assignablePool, setAssignablePool] = useState([]);
  // H1 — "Give to…" hand-off: real-staff-only picker + intent that drives the shared modal.
  const [staffPool, setStaffPool] = useState([]);
  const [giveIntent, setGiveIntent] = useState(null); // { title, subtitle, source_kind, source_id, body, source_url, source_label, client_name, section }
  // Share a news link with a colleague — one button, one email (NOT the hand-off).
  const [shareIntent, setShareIntent] = useState(null); // { news_id, headline, source_url, event_id }
  const [showAddSender, setShowAddSender] = useState(false);
  const [newSender, setNewSender] = useState({email:"",display_name:"",title:""});
  const [showEditTask, setShowEditTask] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({title:"",assigned_to_name:"",status:"",description:""});
  const [closeNote, setCloseNote] = useState("");
  const [showCloseTask, setShowCloseTask] = useState(null);
  const [completingTask, setCompletingTask] = useState(null);
  const [completingOutcome, setCompletingOutcome] = useState(null); // #4 — open completion pre-set to "waiting"
  const [blockAck, setBlockAck] = useState(false); // #4 — Blocked/On Hold "ours or theirs?" acknowledged
  const [editingDraft, setEditingDraft] = useState(false);
  const [editDraftSubject, setEditDraftSubject] = useState("");
  const [editDraftBody, setEditDraftBody] = useState("");
  const [showContentDraftEditor, setShowContentDraftEditor] = useState(null);
  const [contentDraftBody, setContentDraftBody] = useState("");
  const [contentDraftDeadline, setContentDraftDeadline] = useState("");
  const [contentDraftSaving, setContentDraftSaving] = useState(false);
  const [contentDraftSending, setContentDraftSending] = useState(false);
  const [contentDraftRefining, setContentDraftRefining] = useState(false);
  const [contentDraftRefineInstr, setContentDraftRefineInstr] = useState("");
  const [designerConfig, setDesignerConfig] = useState({designer_email:"",designer_name:"",configured:false});
  // Weekly BD Playbook — "work this content theme": open, assign + Rex-draft into Content Studio.
  const [themeWork, setThemeWork] = useState(null); // the content_direction item being worked
  const [themeAssignee, setThemeAssignee] = useState("");
  const [themePostType, setThemePostType] = useState("company");
  const [themeBusy, setThemeBusy] = useState(false);

  const f = useCallback(u=>fetch(`${API}${u}`,{credentials:"include"}),[]);
  const post = useCallback((u,b)=>fetch(`${API}${u}`,{method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify(b)}),[]);

  const openThemeWork = (c) => { setThemeAssignee(""); setThemePostType("company"); setThemeWork(c); };
  const reloadPlaybook = async () => { try { const r = await f("/mission-control/weekly-playbook"); if (r.ok) setWeeklyPlaybook(await r.json()); } catch (e) {} };
  const assignThemeDraft = async () => {
    if (!themeWork || !themeAssignee) return;
    setThemeBusy(true);
    const who = staffPool.find(s => s.email === themeAssignee);
    const r = await post("/content-gen/from-theme", {
      theme: themeWork.theme,
      angle: themeWork.suggested_angle || "",
      post_type: themePostType,
      assigned_to: themeAssignee,
      assigned_to_name: who?.name || themeAssignee.split("@")[0],
      signal_count: themeWork.signal_count || 0,
      sample_headlines: themeWork.sample_headlines || [],
    });
    setThemeBusy(false);
    if (r.ok) {
      toast.success(`Assigned to ${who?.name || "colleague"} — Rex drafted it and it's now in their scrum`);
      setThemeWork(null);
      reloadPlaybook();
    } else {
      toast.error("Could not assign this theme");
    }
  };
  const draftThemeMyself = async () => {
    if (!themeWork) return;
    setThemeBusy(true);
    const r = await post("/content-gen/from-theme", {
      theme: themeWork.theme,
      angle: themeWork.suggested_angle || "",
      post_type: themePostType,
      assigned_to: user?.email || "",
      assigned_to_name: user?.name || (user?.email || "").split("@")[0],
      signal_count: themeWork.signal_count || 0,
      sample_headlines: themeWork.sample_headlines || [],
    });
    setThemeBusy(false);
    if (r.ok) {
      const d = await r.json().catch(() => ({}));
      setThemeWork(null);
      reloadPlaybook();
      navigate(d.draft_id ? `/content-studio?draft=${d.draft_id}` : "/content-studio");
    } else {
      toast.error("Could not start this draft");
    }
  };

  // G1 — commitment card helpers (Today "Reviews requiring attention")
  const refetchCommits = useCallback(async () => {
    try { const r = await f("/scrum/v2/commitments/today"); if (r.ok) setCommits(await r.json()); } catch (e) {}
  }, [f]);
  const commitUpdate = useCallback(async (actionId, body, okMsg) => {
    const r = await post(`/scrum/v2/commitments/${actionId}/update`, body);
    if (r.ok) { toast.success(okMsg); setCommitUI(null); await refetchCommits(); }
    else { const e = await r.json().catch(()=>({})); toast.error(e.detail || e.message || "Could not update"); }
  }, [post, refetchCommits]);
  const resolveCommitClient = useCallback(async (action, chosenId=null) => {
    const r = await post(`/scrum/v2/commitments/${action.action_id}/resolve-client`, chosenId ? { client_id: chosenId } : {});
    const d = await r.json().catch(()=>({}));
    if (d.match_state === "resolved" && d.client_id) { setCommitUI(null); openClient(d.client_id); await refetchCommits(); }
    else if (d.match_state === "ambiguous") { setCommitUI({ id: action.action_id, mode: "disambig", candidates: d.candidates || [] }); }
    else { setCommitUI({ id: action.action_id, mode: "disambig", candidates: [] }); }
  }, [post, openClient, refetchCommits]);
  // Headlines must open something actionable. Route a commitment to the best
  // available workspace: client drawer > source item > link-a-client flow.
  const openCommitment = useCallback((a) => {
    if (a.linked_client_id) { openClient(a.linked_client_id); return; }
    if (a.source_url) { window.open(a.source_url, "_blank", "noopener,noreferrer"); return; }
    const internal = commitmentInternalPath(a);
    if (internal) { navigate(internal); return; }
    if (a.linked_client_name) { resolveCommitClient(a); return; }
    toast("Nothing linked to open yet — add a client or a source to this item");
  }, [openClient, resolveCommitClient, navigate]);
  // N3 Commit B — reuse the existing client search + prospect flow to resolve an alert with no client.
  const openClientMatch = useCallback(async (entity) => {
    setClientMatch({ entity }); setMatchResults([]); setMatchLoading(true);
    try {
      const r = await fetch(`${API}/scrum/v2/clients/search?q=${encodeURIComponent(entity)}`, { credentials: "include" });
      if (r.ok) setMatchResults(await r.json());
    } finally { setMatchLoading(false); }
  }, []);
  const trackProspect = useCallback(async (entity) => {
    const r = await fetch(`${API}/prospects`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ company_name: entity, source: "weekly_bd_playbook" }) });
    if (r.ok) { toast.success(`Now watching ${entity} — nothing added to the client book`); setClientMatch(null); }
    else toast.error("Couldn't track prospect");
  }, []);
  const put = useCallback((u,b)=>fetch(`${API}${u}`,{method:"PUT",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify(b)}),[]);

  // REX-004B + REX-BRIEF-EVERYWHERE — wrapper around POST /analyst-tasks/route.
  // Pops the shared <AssignWithBriefModal> so the assigner can ALWAYS pick
  // who gets the task and leave a brief explaining the angle — even when
  // routing to themselves (user pick 2a: "yes pop modal because I might not
  // remember what I wanted to do"). Returns the spawned task on confirm,
  // null on cancel or failure. Existing callers awaiting this still work.
  const routeToLensSmart = useCallback((payload) => {
    return new Promise((resolve) => {
      const headline = payload?.source_payload?.headline || payload?.title || "Lens task";
      setRouteIntent({
        title: "Route this to a teammate",
        subtitle: headline,
        suggestedBrief: buildBriefFromPayload(payload),
        confirmLabel: "Route with brief",
        onCancel: () => { resolve(null); },
        onConfirm: async ({ assignee_email, brief_for_assignee }) => {
          try {
            const body = { ...payload };
            if (assignee_email) body.assignee_email = assignee_email;
            if (brief_for_assignee) body.brief_for_assignee = brief_for_assignee;
            const r = await post("/analyst-tasks/route", body);
            if (!r.ok) {
              toast.error("Couldn't route to Lens");
              resolve(null);
              return true;
            }
            const d = await r.json();
            resolve(d.task || null);
            return true;
          } catch {
            toast.error("Network");
            resolve(null);
            return true;
          }
        },
      });
    });
  }, [post]);

  // REX-004B — confirm the analyst picker selection. PATCHes the task with
  // the chosen email then closes the dialog.
  const confirmAssign = useCallback(async () => {
    if (!assignPrompt?.task_id || !assignChoice) {
      setAssignPrompt(null);
      return;
    }
    try {
      const r = await fetch(`${API}/analyst-tasks/${assignPrompt.task_id}/assign`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assignee_email: assignChoice }),
      });
      if (r.ok) toast.success(`Assigned to ${assignChoice}`);
      else toast.error("Couldn't assign");
    } catch { toast.error("Network"); }
    setAssignPrompt(null);
    setAssignChoice("");
  }, [assignPrompt, assignChoice]);

  const act = async (u,m="POST",b=null,msg="Done") => {
    try { const opts={method:m,credentials:"include"}; if(b){opts.headers={"Content-Type":"application/json"};opts.body=JSON.stringify(b);} const r=await fetch(`${API}${u}`,opts); if(r.ok) toast.success(msg); else{const e=await r.json().catch(()=>({}));toast.error(e.detail||"Failed");}} catch{toast.error("Error");}
  };
  // Remove a task from every Today list the moment its save succeeds; keep them in sync.
  const removeTaskFromLists = (taskId) => {
    setExecBoard(prev => {
      const lanes = {}; let removed = 0;
      for (const k of Object.keys(prev.lanes || {})) { const b = prev.lanes[k] || []; lanes[k] = b.filter(x => x.task_id !== taskId); removed += b.length - lanes[k].length; }
      return { ...prev, lanes, total: Math.max(0, (prev.total || 0) - removed) };
    });
    setScrumTasks(p => p.filter(x => x.task_id !== taskId));
    setOps(p => p.filter(x => x.task_id !== taskId));
  };
  const completeTask = async (t) => {
    // C1: no silent completion — open the shared completion step.
    setCompletingTask(t);
  };
  // #4 — "Now with them": open the SAME completion step pre-set to the waiting conversion,
  // so a task that's now with someone else comes off the board as a real wait (no new mechanism).
  const nowWithThem = (t) => {
    if (!t) return;
    setShowEditTask(null);
    setCompletingOutcome("waiting");
    setCompletingTask(t);
  };
  useEffect(() => {
    // N3 Commit C — a task changed anywhere: drop it optimistically if completed, then refetch.
    const onDone = (e) => { const id = e.detail && e.detail.task_id; if (id) removeTaskFromLists(id); fetchAll(); };
    window.addEventListener("scrum:task-completed", onDone);
    return () => window.removeEventListener("scrum:task-completed", onDone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dismissNews = id => { setNewsItems(p=>({today:p.today.filter(n=>n.news_id!==id),previous:p.previous.filter(n=>n.news_id!==id)})); fetch(`${API}/mission-control/morning-news/${id}/dismiss`,{method:"POST",credentials:"include"}).then(r=>{if(r.ok)toast.success("Dismissed");else toast.error("Failed");}); };
  const dismissSignal = id => { setSignals(p=>p.filter(s=>s.signal_id!==id)); fetch(`${API}/mission-control/signals/${id}/dismiss`,{method:"POST",credentials:"include"}).then(r=>{if(r.ok)toast.success("Dismissed");}); };
  // Market Signals refresh — after a triage action the row stays inspectable (re-renders
  // with its new status, e.g. "accepted"); nothing is removed just because a local array filtered.
  // Keep handed_off signals in the list — they are shown DISTINCTLY as "Assigned to {name}"
  // so an assignment never silently vanishes. Only truly-closed states (dismissed/sent) drop.
  const refreshSignals = useCallback(async ()=>{ const r=await f("/mission-control/signals"); if(r.ok){ const d=await r.json(); setSignals((d.signals||[]).filter(s=>!["dismissed","sent"].includes(s.status))); } },[]);
  const openSignalHistory = async (sig) => {
    setSignalHistory({ sig, loading: true, rows: [] });
    const r = await f(`/triage-history/${sig.signal_id}`);
    const d = r.ok ? await r.json() : { history: [] };
    setSignalHistory({ sig, loading: false, rows: d.history || [] });
  };
  // Part 4 — events carry their decision on Today. Plan reuses the existing posture
  // vocabulary (attend / speak / sponsor); dismiss and create reuse existing endpoints.
  const dismissEvent = id => { setEvents(p=>p.filter(e=>e.event_id!==id)); setEventsTotal(n=>Math.max(0,n-1)); fetch(`${API}/events/${id}/dismiss`,{method:"POST",credentials:"include"}).then(r=>{if(r.ok)toast.success("Event dismissed");else toast.error("Failed");}); };
  const setEventPosture = async (id, posture) => { const r = await fetch(`${API}/events/${id}/posture`,{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify({posture})}); if(r.ok){ setEvents(p=>p.map(e=>e.event_id===id?{...e,our_posture:posture,posture_set_at:new Date().toISOString()}:e)); toast.success(posture==="attend"?"Planning to attend":`Planning to ${posture}`);} else toast.error("Failed to set plan"); };
  const addEvent = async () => { if(!newEvent.name||!newEvent.date_start||!newEvent.country){ toast.error("Name, start date and country are required"); return; } const r = await fetch(`${API}/events/create`,{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify(newEvent)}); if(r.ok){ toast.success("Event added"); setShowAddEvent(false); setNewEvent({ name:"", date_start:"", country:"", city:"", url:"", description:"" }); fetchAll(); } else { const e=await r.json().catch(()=>({})); toast.error(e.detail||"Failed to add event"); } };
  // Reuses the existing POST /content-calendar/markers (create_marker). An occasion you
  // add behaves exactly like one Rex found — appears in date order with the same exits.
  const addOccasion = async () => { if(!newOccasion.date||!newOccasion.title){ toast.error("Date and title are required"); return; } const r = await fetch(`${API}/content-calendar/markers`,{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify(newOccasion)}); if(r.ok){ toast.success("Occasion added"); setShowAddOccasion(false); setNewOccasion({ date:"", title:"", type:"custom", country:"", notes:"", content_opportunity:true }); fetchAll(); } else { const e=await r.json().catch(()=>({})); toast.error(e.detail||"Failed to add occasion"); } };
  const dismissMotion = (clientId) => {
    setClientMotion(p => p.filter(c => c.client_id !== clientId));
    fetch(`${API}/mission-control/client-motion/${clientId}/dismiss`, { method: "POST", credentials: "include" }).then(r => { if (r.ok) toast.success("Motion dismissed"); });
  };
  const dismissTender = id => { setTenders(p=>p.filter(t=>t.tender_id!==id)); fetch(`${API}/tenders/${id}/interest`,{method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify({interest:"dismiss"})}).then(r=>{if(r.ok)toast.success("Dismissed");}); };
  const commit = (title,sec,client="") => { setCf({title,source_section:sec,due_time:"17:00",client_name:client,founder_escalation:sec==="founder",sender_id:""}); setShowCommit(true); };

  // H1 — open the shared "Give to…" modal for any Today item. `intent` carries everything
  // that must travel with the card (title, full body/generated text, source link, client).
  const openGive = (intent) => setGiveIntent(intent);
  // Share a link: open the small dialog. A stable event_id per open makes a retried
  // Send idempotent (no double-record, no double-email) without blocking a later,
  // genuinely different share to the same colleague.
  const openShare = (n) => setShareIntent({
    news_id: n.news_id,
    headline: n.headline || n.title || "",
    source_url: n.source_url || "",
    event_id: `share_${n.news_id}_${Date.now()}`,
  });
  const doShare = async ({ assignee_email, brief_for_assignee }) => {
    if (!shareIntent) return true;
    if (!assignee_email) { toast.error("Choose a colleague to send this to."); return false; }
    try {
      const r = await post("/scrum/v2/share", {
        news_id: shareIntent.news_id,
        headline: shareIntent.headline,
        source_url: shareIntent.source_url,
        recipient_email: assignee_email,
        context: brief_for_assignee || "",
        event_id: shareIntent.event_id,
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); toast.error(e.detail || "Couldn't send the link"); return false; }
      const d = await r.json();
      toast.success(`Link sent to ${d.shared_with}`);
      return true;
    } catch { toast.error("Network error"); return false; }
  };
  const doHandoff = async ({ assignee_email, brief_for_assignee }) => {
    if (!giveIntent) return true;
    if (!assignee_email) { toast.error("Choose a person to hand this to."); return false; }
    try {
      const r = await post("/scrum/v2/handoff", {
        source_kind: giveIntent.source_kind,
        source_id: giveIntent.source_id || "",
        title: giveIntent.title || "",
        body: giveIntent.body || "",
        note: brief_for_assignee || "",
        source_url: giveIntent.source_url || "",
        source_label: giveIntent.source_label || "",
        client_name: giveIntent.client_name || "",
        assignee_email,
        section: giveIntent.section || "commercial",
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); toast.error(e.detail || "Could not hand over"); return false; }
      const who = (staffPool.find(s => s.email === assignee_email)?.name) || assignee_email.split("@")[0];
      toast.success(`Given to ${who} — it's on their Today list`);
      // Remove the handed item from its Today source list so it stops showing as unhandled.
      if (giveIntent.source_kind === "campaign" && giveIntent.source_id) setCampaigns(p => p.filter(c => c.campaign_id !== giveIntent.source_id));
      else if (giveIntent.source_id) { setNewsItems(p => ({ today: p.today.filter(n => n.news_id !== giveIntent.source_id), previous: p.previous.filter(n => n.news_id !== giveIntent.source_id) })); }
      await refetchCommits();
      return true;
    } catch { toast.error("Network error"); return false; }
  };

  const openContentDraftEditor = async (item) => {
    if (item._src !== "studio") {
      // Flywheel items don't have the same edit/send-to-designer flow — fallback to deep link
      navigate(`/amplify?draft=${item.item_id}`);
      return;
    }
    setShowContentDraftEditor(item);
    setContentDraftBody(item.full_content || "");
    setContentDraftRefineInstr("");
    const dflt = new Date(Date.now() + 2 * 86400000).toISOString().split("T")[0];
    setContentDraftDeadline(dflt);
    // Fetch designer config (for send-to-designer button state)
    try {
      const r = await f("/content-gen/config/designer");
      if (r.ok) setDesignerConfig(await r.json());
    } catch (e) { /* ignore */ }
  };

  const saveContentDraft = async () => {
    if (!showContentDraftEditor) return;
    setContentDraftSaving(true);
    const r = await put(`/content-gen/drafts/${showContentDraftEditor.item_id}`, { content: contentDraftBody });
    setContentDraftSaving(false);
    if (r.ok) {
      const updated = await r.json();
      toast.success(updated.user_amended ? "Saved — Rex will learn from your edits" : "Draft saved");
      setContentItems(p => p.map(x => x.item_id === showContentDraftEditor.item_id ? { ...x, full_content: contentDraftBody, primary_draft: { hook: contentDraftBody.substring(0,80) } } : x));
      setShowContentDraftEditor(p => p ? { ...p, full_content: contentDraftBody } : p);
    } else {
      toast.error("Save failed");
    }
  };
  const openNudgeDialog = async () => {
    if (!heatmapDetail) return;
    setNudgeOpen(true);
    setNudgeSubject("");
    setNudgeBody("");
    setNudgeExtra("");
    setNudgeTone("friendly");
    // Auto-draft
    setNudgeDrafting(true);
    try {
      const r = await post("/content-studio/designer-nudge/preview", {
        designer_id: heatmapDetail.designer_id || "",
        designer_name: heatmapDetail.designer_name || "",
        tone: "friendly",
      });
      if (r.ok) {
        const d = await r.json();
        setNudgeSubject(d.subject || "");
        setNudgeBody(d.body || "");
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Could not draft nudge");
      }
    } catch (e) { toast.error("Draft failed"); }
    setNudgeDrafting(false);
  };

  const redraftNudge = async () => {
    if (!heatmapDetail) return;
    setNudgeDrafting(true);
    try {
      const r = await post("/content-studio/designer-nudge/preview", {
        designer_id: heatmapDetail.designer_id || "",
        designer_name: heatmapDetail.designer_name || "",
        tone: nudgeTone,
        extra_note: nudgeExtra,
      });
      if (r.ok) {
        const d = await r.json();
        setNudgeSubject(d.subject || "");
        setNudgeBody(d.body || "");
        toast.success("REX redrafted the nudge");
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Redraft failed");
      }
    } catch (e) { toast.error("Redraft failed"); }
    setNudgeDrafting(false);
  };

  const sendNudge = async () => {
    if (!heatmapDetail || !nudgeSubject.trim() || !nudgeBody.trim()) return;
    setNudgeSending(true);
    try {
      const r = await post("/content-studio/designer-nudge/send", {
        designer_id: heatmapDetail.designer_id || "",
        designer_name: heatmapDetail.designer_name || "",
        subject: nudgeSubject,
        body: nudgeBody,
        tone: nudgeTone,
        extra_note: nudgeExtra,
      });
      if (r.ok) {
        const d = await r.json();
        if (d.delivery_status === "sent") toast.success(`Nudge sent to ${d.designer_email}`);
        else if (d.warning) toast.warning(d.warning);
        else toast.success("Nudge logged");
        setNudgeOpen(false);
        setHeatmapDetail(null);
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Send failed");
      }
    } catch (e) { toast.error("Send failed"); }
    setNudgeSending(false);
  };

  const openChaseDialog = async (item) => {
    const taskId = item?.target_id;
    if (!taskId) return;
    setChaseTask(item);
    setChaseOpen(true);
    setChaseDrafting(true);
    setChase({ subject: "", body: "", to_email: "", to_name: "", client_name: item.client_name || "", senders: [], sender_id: "" });
    try {
      const r = await post(`/scrum/v2/tasks/${taskId}/chase/draft`, { tone: "friendly" });
      if (r.ok) {
        const d = await r.json();
        setChase({
          subject: d.subject || "", body: d.body || "",
          to_email: d.to_email || "", to_name: d.to_name || "",
          client_name: d.client_name || "", senders: d.senders || [],
          sender_id: d.default_sender_id || (d.senders?.[0]?.sender_id || ""),
        });
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Could not draft the chase");
        setChaseOpen(false);
      }
    } catch (e) { toast.error("Draft failed"); setChaseOpen(false); }
    setChaseDrafting(false);
  };

  const redraftChase = async () => {
    const taskId = chaseTask?.target_id;
    if (!taskId) return;
    setChaseDrafting(true);
    try {
      const r = await post(`/scrum/v2/tasks/${taskId}/chase/draft`, { tone: "friendly" });
      if (r.ok) {
        const d = await r.json();
        setChase((c) => ({ ...c, subject: d.subject || "", body: d.body || "" }));
        toast.success("Rex redrafted the chase");
      }
    } catch (e) { toast.error("Redraft failed"); }
    setChaseDrafting(false);
  };

  const sendChase = async () => {
    const taskId = chaseTask?.target_id;
    if (!taskId || !chase.subject.trim() || !chase.body.trim()) return;
    if (!chase.to_email.trim()) { toast.error("No client email to send to"); return; }
    if (!chase.sender_id) { toast.error("Pick a sender to send from"); return; }
    setChaseSending(true);
    try {
      const r = await post(`/scrum/v2/tasks/${taskId}/chase/send`, {
        sender_id: chase.sender_id, to_email: chase.to_email, to_name: chase.to_name,
        subject: chase.subject, body: chase.body,
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(d.status === "sent" ? `Chase sent to ${d.to_email}` : "Chase queued to send");
        setChaseOpen(false); setChaseTask(null);
        refreshAtRiskQueue && refreshAtRiskQueue();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Send failed");
      }
    } catch (e) { toast.error("Send failed"); }
    setChaseSending(false);
  };

  const refineContentDraft = async () => {
    if (!showContentDraftEditor || !contentDraftRefineInstr.trim()) return;
    setContentDraftRefining(true);
    const r = await post(`/content-gen/drafts/${showContentDraftEditor.item_id}/refine`, {
      instruction: contentDraftRefineInstr.trim(),
      current_content: contentDraftBody,
    });
    setContentDraftRefining(false);
    if (r.ok) {
      const updated = await r.json();
      setContentDraftBody(updated.content || "");
      setContentItems(p => p.map(x => x.item_id === showContentDraftEditor.item_id ? { ...x, full_content: updated.content || "", primary_draft: { hook: (updated.content || "").substring(0, 80) } } : x));
      setContentDraftRefineInstr("");
      toast.success("REX refined the post");
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Refine failed");
    }
  };

  const sendContentDraftToDesigner = async () => {    if (!showContentDraftEditor) return;
    if (!designerConfig.configured) { toast.error("Designer email not configured. Set it in Content Studio → Settings."); return; }
    // Save first if body changed
    if (contentDraftBody !== (showContentDraftEditor.full_content || "")) {
      await saveContentDraft();
    }
    setContentDraftSending(true);
    const r = await post(`/content-gen/drafts/${showContentDraftEditor.item_id}/send-to-designer`, { completion_date: contentDraftDeadline });
    setContentDraftSending(false);
    if (r.ok) {
      const result = await r.json();
      toast.success(result.sent ? `Sent to ${result.designer_email}. Card on Production Board.` : (result.message || "Pushed to Production Board"));
      setContentItems(p => p.map(x => x.item_id === showContentDraftEditor.item_id ? { ...x, status: "sent_to_designer", sent_to_designer: true } : x));
      setShowContentDraftEditor(null);
      // Refresh heatmap so the new card shows up
      try { const hr = await f("/content-studio/designer-heatmap"); if (hr.ok) setDesignerHeatmap(await hr.json()); } catch (e) { /* ignore */ }
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Send failed");
    }
  };

  const handleActionRoute = async () => {
    if (!showActionRoute || !actionRouteChoice) return;
    const news = showActionRoute;
    const company = (news.organisations || [])[0] || news.company || "";
    const headline = news.headline || "";
    const chosen = actionRouteChoice;

    // Close the action-route dialog FIRST so the brief modal can take focus.
    setShowActionRoute(null);
    setActionRouteChoice("");
    setActionRouteNote("");

    // News Triage Overhaul (Feb 2026): every triage button also spawns an
    // analyst_task that lands on the Lens (Analyst Queue) Kanban board with
    // an SLA. Non-blocking — failures here MUST NOT break legacy commitments.
    // REX-BRIEF-EVERYWHERE (May 2026): each spawn now ALWAYS pops the
    // assign-with-brief modal first (user setting 2a) — the spawn awaits
    // the user confirming or cancelling before continuing the flow.
    const _spawnLensTask = async (triageAction, title) => {
      return await routeToLensSmart({
        triage_action: triageAction,
        title,
        source_news_id: news.news_id || news.article_id || null,
        source_payload: {
          source_kind: "news",
          headline,
          company,
          country: news.country || "",
          importance: news.importance || "",
          source_url: news.source_url || "",
          category: news.category || "",
          organisations: news.organisations || [],
        },
      });
    };

    if (chosen === "research_crm") {
      await post("/scrum/v2/commitments", { title: `Research & add to CRM: ${company} — ${headline.substring(0,50)}`, source_section: "commercial", due_time: "17:00", client_name: company, founder_escalation: false });
      post("/ask-rex/chat", { message: `Research the company "${company}" based on this news: "${headline}". Summarise who they are, the commercial angle, and likely points of contact, and report back. Do NOT create a client record or any CRM object — this is research only.`, session_id: "scrum_research" });
      await _spawnLensTask("research_crm", `Research & add to CRM: ${company} — ${headline.substring(0,80)}`);
      toast.success(`Commitment created + Rex researching ${company}`);
    } else if (chosen === "add_to_pipeline") {
      toast.info("Estimating deal value with Rex…");
      const payload = {
        headline, company,
        importance: news.importance || "",
        country: news.country || "",
        source_url: news.source_url || "",
        trigger_category: news.category || "",
        organisations: news.organisations || [],
      };
      const r = await post("/mission-control/signal-to-pipeline", payload);
      if (r.ok) {
        const d = await r.json();
        const fmtNGN = new Intl.NumberFormat("en-NG", { style: "currency", currency: d.currency || "NGN", maximumFractionDigits: 0 }).format(d.estimated_value || 0);
        await _spawnLensTask("add_to_pipeline", `Confirm pipeline value: ${company} — ${headline.substring(0,60)}`);
        toast.success(`Pipeline opportunity created — ${fmtNGN} (${d.confidence_label} confidence)`, {
          duration: 6000,
          action: { label: "Open Pipeline", onClick: () => window.location.assign("/revenue") },
        });
      } else if (r.status === 409) {
        const e = await r.json().catch(() => ({}));
        const det = e?.detail || {};
        setPipelineAsk({ company, headline, payload, candidates: det.candidates || [] });
      } else {
        toast.error("Failed to create pipeline entry");
      }
    } else if (chosen === "conversation_entry") {
      setConvEntryLoading(true);
      toast.info("Rex is crafting a conversation entry...");
      const r = await post("/mission-control/conversation-entry", {
        headline: headline, company: company,
        importance: news.importance || "", country: news.country || "",
        source_url: news.source_url || "",
        trigger_category: news.category || "",
      });
      setConvEntryLoading(false);
      if (r.ok) {
        const record = await r.json();
        const cm = clientHealth.find(c=>c.company_name?.toLowerCase()===String(company||"").toLowerCase());
        setShowConvEntry({ ...record, company, headline, news_id: news.news_id || news.article_id || null });
        setConvSave({ assignee_name: "", client_name: company || "", client_id: cm?.client_id || "", saving: false });
        await post("/scrum/v2/commitments", { title: `Initiate conversation: ${company} — ${headline.substring(0,40)}`, source_section: "commercial", due_time: "17:00", client_name: company, founder_escalation: false });
        toast.success("Conversation entry ready");
      } else {
        toast.error("Generation failed — try again");
      }
    } else if (chosen === "create_content") {
      await post("/mission-control/flywheel", { strategic_purpose: "market_authority", content_type: "linkedin_post", brand_mode: "remi_only", context_title: headline, context_summary: news.importance || "", timing: "publish_now" });
      await post("/scrum/v2/commitments", { title: `Review content: ${headline.substring(0,50)}`, source_section: "marketing", due_time: "17:00", client_name: "", founder_escalation: false });
      toast.success("Content item + commitment created");
    } else if (chosen === "track_only") {
      await post("/scrum/v2/commitments", { title: headline, source_section: "commercial", due_time: "17:00", client_name: company, founder_escalation: false });
      toast.success("Commitment created");
    } else if (chosen === "brief_team") {
      await post("/scrum/v2/commitments", { title: `Brief team: ${headline}`, source_section: "general", due_time: "09:30", client_name: "", founder_escalation: false });
      toast.success("Added to team briefing");
    }

    // Dismiss the news item after actioning (Lens task is in flight; this
    // is best-effort and runs even if the user cancelled the brief modal).
    dismissNews(news.news_id);
  };

  const handleRunUpdate = async () => { setUpdating(true); toast.info("Running full scrum update..."); try { await post("/mission-control/run-scrum-update",{}); toast.success("Update complete"); await fetchAll(); } catch{toast.error("Error");} setUpdating(false); };

  const fetchAll = useCallback(async () => {
    if(!loaded.current) setLoading(true);
    try {
      // Phase 1: Fast — lightweight endpoints only (no heavy enrichment)
      const [cmdR, newsR, sigR, comR, tendR, sndR, campR, movesR, playbookR, healthR, riskR, execR, payR, heatmapR, replR, occR, lensR] = await Promise.all([
        f(`/mission-control/command-surface?role=${role}`),
        f("/mission-control/morning-news"),
        f("/mission-control/signals"),
        f("/scrum/v2/commitments/today"),
        f("/tenders?limit=30"),
        f("/mission-control/senders"),
        f("/mission-control/campaigns"),
        f("/mission-control/top-moves"),
        f("/mission-control/weekly-playbook"),
        f("/mission-control/client-health?limit=80"),
        f("/mission-control/at-risk-strip?max_items=7"),
        f("/mission-control/execution-board"),
        f("/opportunities/metrics/payments-due?days=7"),
        f("/content-studio/designer-heatmap"),
        f("/mission-control/pipeline-replenishment?days=7&limit=3"),
        f("/content-calendar/upcoming-opportunities?days=60"),
        f("/analyst-tasks/lane-counts"),
      ]);
      if(cmdR.ok){const d=await cmdR.json();setMomentum(d.momentum);setFounderQ(d.founder_attention||[]);}
      if(newsR.ok){const d=await newsR.json();setNewsItems({today:d.today||[],previous:d.previous||[]});setMonitorCount(d.monitor_count||0);}
      if(movesR.ok){const d=await movesR.json();setTopMoves(d.moves||[]);}
      if(riskR.ok){setAtRisk(await riskR.json());}
      if(execR.ok){setExecBoard(await execR.json());}
      if(payR.ok){setPaymentsDue(await payR.json());}
      if(heatmapR.ok){setDesignerHeatmap(await heatmapR.json());}
      if(lensR.ok){setLensTile(await lensR.json());}
      if(playbookR.ok){setWeeklyPlaybook(await playbookR.json());}
      if(healthR.ok){const d=await healthR.json();setClientHealth(d.clients||[]);}
      if(sigR.ok){const d=await sigR.json();setSignals((d.signals||[]).filter(s=>!["dismissed","sent"].includes(s.status)));}
      if(replR.ok){const d=await replR.json();setReplenishment({items: d.items || [], loaded: true});}
      if(occR.ok){const d=await occR.json();setOccasions({items: d.items || [], loaded: true});}
      if(tendR.ok){const d=await tendR.json();setTenders(d.tenders||[]);}
      if(sndR.ok){const d=await sndR.json();setSenders((d.senders||[]).filter(s=>s.is_active));}
      if(campR.ok){const d=await campR.json();setCampaigns((d.campaigns||[]).filter(c=>c.status!=="rejected"));}
      if(comR.ok) setCommits(await comR.json());
      try { const bvR = await f(`/scrum/v2/board-views${user?.user_id ? `?person_id=${user.user_id}` : ""}`); if (bvR.ok) setMyBoard(await bvR.json()); } catch (e) {}
      // REX-BRIEF (May 2026) — load the assignable pool for the
      // Upcoming-Occasions assign-with-brief modal. Cheap (cached at backend).
      fetch(`${API}/analyst-tasks/analysts`, { credentials: "include" })
        .then(r => r.ok ? r.json() : { analysts: [] })
        .then(d => setAssignablePool(d.analysts || []))
        .catch(() => { /* silently */ });
      // H1 — real-staff-only pool for the "Give to…" hand-off picker.
      fetch(`${API}/scrum/v2/assignable-staff`, { credentials: "include" })
        .then(r => r.ok ? r.json() : { staff: [] })
        .then(d => setStaffPool(d.staff || []))
        .catch(() => { /* silently */ });
      loaded.current = true;
      setLoading(false);

      // Phase 2: Background — heavy scrum-data + flywheel + content studio
      const [scrumR, fwR, csR] = await Promise.all([
        f("/mission-control/scrum-data"),
        f("/mission-control/flywheel"),
        f("/content-gen/drafts"),
      ]);
      if(scrumR.ok){
        const d=await scrumR.json();
        setClientMotion(d.client_motion||[]);
        setProposals(d.proposals||[]);
        setScrumTasks(d.scrum_tasks||[]);
        setOps(d.ops_tasks||[]);
        setEvents(d.upcoming_events||[]);
        setEventsTotal(d.upcoming_events_total ?? (d.upcoming_events||[]).length);
      }
      let allContent = [];
      if(fwR.ok){const d=await fwR.json(); allContent.push(...(d.items||[]).map(i=>({...i,_src:"flywheel"})));}
      if(csR.ok){const d=await csR.json(); const drafts=Array.isArray(d)?d:d.drafts||[]; allContent.push(...drafts.map(d=>({item_id:d.draft_id||d._id,context_title:d.topic||d.title||"Draft",status:d.status||"draft",brand_mode:d.voice||"remi_only",content_type:d.content_type||"linkedin_post",primary_draft:d.content?{hook:d.content.substring(0,80)}:null,full_content:d.content||"",post_type:d.post_type||"company",sent_to_designer:!!d.sent_to_designer,_src:"studio"})));}
      // Deduplicate by title — prefer studio drafts over flywheel items
      const seen=new Set();const deduped=[];
      allContent.sort((a,b)=>a._src==="studio"?-1:1);
      for(const item of allContent){const key=(item.context_title||"").toLowerCase().trim().substring(0,40);if(key&&seen.has(key))continue;if(key)seen.add(key);deduped.push(item);}
      setContentItems(deduped.filter(i=>!["archived","posted","published","learning"].includes(i.status)));
    } catch(e){console.error(e);setLoading(false);}
  },[role,f]);

  // Load once on mount, not on every render
  useEffect(()=>{fetchAll();},[fetchAll]);

  // At Risk doorway: open the full filtered queue behind the strip's counts.
  const openAtRiskQueue = useCallback(async (type, label) => {
    setAtRiskFilter({ type, label });
    setAtRiskQueue({ items: [], total_items: 0, loading: true });
    const qs = type && type !== "all" ? `?type=${type}` : "";
    const r = await fetch(`${API}/scrum/v2/at-risk${qs}`, { credentials: "include" });
    const b = await r.json().catch(() => ({ items: [], total_items: 0 }));
    setAtRiskQueue({ items: b.items || [], total_items: b.total_items || 0, loading: false });
  }, []);
  const refreshAtRiskQueue = useCallback(async () => {
    setAtRiskFilter((f) => {
      if (f) {
        const qs = f.type && f.type !== "all" ? `?type=${f.type}` : "";
        fetch(`${API}/scrum/v2/at-risk${qs}`, { credentials: "include" })
          .then((r) => r.json()).catch(() => ({ items: [] }))
          .then((b) => setAtRiskQueue({ items: b.items || [], total_items: b.total_items || 0, loading: false }));
      }
      return f;
    });
    fetchAll();
  }, [fetchAll]);

  // ONE destination resolver for At Risk "Open" (executing elsewhere), shared by the
  // strip and the filtered queue. No mutations here — navigation / drawer only.
  const openAtRiskItem = useCallback((item) => {
    if (item.target_type === "client" && item.target_id) { openClient(item.target_id); return; }
    if (item.target_type === "campaign") { const c = campaigns.find(x => x.campaign_id === item.target_id); if (c) setShowDraftPreview(c); else navigate("/mission-control"); return; }
    if (item.target_type === "content_card") { navigate("/amplify"); return; }
    if (item.target_type === "task" && item.target_id) { navigate("/scrum"); return; }
    toast.info(item.title || "Item");
  }, [campaigns, openClient, navigate]);

  useEffect(() => {
    let live = true;
    fetch(`${API}/waits/today-counts`, { credentials: "include", cache: "no-store" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (live && d) setWaitCounts(d); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // N6 — email-health line: only renders when something is wrong (failing / may_stopped / cannot_tell).
  useEffect(() => {
    let live = true;
    fetch(`${API}/today/email-health`, { credentials: "include", cache: "no-store" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (live && d) setEmailHealth(d); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // G1 — users list for the commitment reassign picker.
  useEffect(() => {
    let live = true;
    fetch(`${API}/users`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (live && Array.isArray(d)) setCommitUsers(d); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  if(loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-blue-500"/></div>;

  const flushCaches = async () => {
    try {
      const r = await fetch(`${API}/admin-console/cache/invalidate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (r.ok) {
        const d = await r.json();
        // Also clear local client-side cache (revenue snapshot, etc.)
        ttlCacheClear();
        toast.success(`Server cache flushed (${d.cleared} entries) — refreshing…`);
        fetchAll();
      } else if (r.status === 403) {
        toast.error("Cache flush requires admin, editor or scrum-master access.");
      } else {
        toast.error("Cache flush failed");
      }
    } catch {
      toast.error("Cache flush failed");
    }
  };

  const canFlushCache = user && ["admin", "user"].includes(user.role);

  // #3 — the banner's "View" opens the honest outbox: what was sent, when, to whom, and what
  // is actually known about each message (never an invented "delivered").
  const openEmailActivity = async () => {
    setShowEmailActivity(true);
    setEmailActivity(null);
    try {
      const r = await fetch(`${API}/today/email-activity`, { credentials: "include", cache: "no-store" });
      if (r.ok) setEmailActivity(await r.json());
    } catch { /* leave null → modal shows a load error */ }
  };

  return (
    <div className="space-y-3" data-testid="morning-scrum-os">
      {/* N6 — email-health line: only when something is wrong. No green/all-well state. */}
      {emailHealth && emailHealth.state && (
        <button
          onClick={openEmailActivity}
          data-testid="today-email-health-line"
          data-state={emailHealth.state}
          className={`w-full text-left text-sm rounded-lg px-4 py-2 flex items-center gap-2 border transition-colors ${
            emailHealth.severity === "amber"
              ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
              : "bg-rose-50 border-rose-300 text-rose-800 hover:bg-rose-100"
          }`}
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{emailHealth.message}</span>
          {typeof emailHealth.count === "number" && (
            <strong data-testid="today-email-health-count" className="ml-1">{emailHealth.count}</strong>
          )}
          <span className="ml-auto text-xs underline">View →</span>
        </button>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-slate-900">Morning Scrum OS</h1>
          {momentum&&<div className={`px-4 py-1.5 rounded-full text-sm font-semibold ${momentum.score>=65?"bg-emerald-100 text-emerald-700":momentum.score>=40?"bg-amber-100 text-amber-700":"bg-slate-100 text-slate-600"}`}>{momentum.score} — {momentum.description}</div>}
          {emailCount > 0 && <div data-testid="today-email-count" className="px-3 py-1.5 rounded-full text-sm font-semibold bg-[#174987]/10 text-[#174987] flex items-center gap-1"><Mail className="h-4 w-4" />{emailCount} from your emails</div>}
        </div>
        <div className="flex items-center gap-2">
          {canFlushCache && (
            <Button
              variant="ghost"
              size="sm"
              onClick={flushCaches}
              title="Force-refresh all Revenue / Amplify / Brand data from Neon (use if numbers look stale)"
              className="text-slate-500 hover:text-slate-900"
              data-testid="today-flush-cache-btn"
            >
              <RefreshCw className="h-4 w-4 mr-1"/>Refresh data
            </Button>
          )}
          <Button variant="outline" onClick={handleRunUpdate} disabled={updating}>{updating?<Loader2 className="h-4 w-4 animate-spin mr-1"/>:<Play className="h-4 w-4 mr-1"/>}Run Scrum Update</Button>
          {senders.length===0&&<Button size="sm" variant="outline" className="text-amber-600 border-amber-300" onClick={()=>setShowAddSender(true)}>Setup Senders</Button>}
        </div>
      </div>

      {/* Waits — quiet line linking to Triage (Sep 2026) */}
      {(waitCounts.waiting > 0 || waitCounts.chase_today > 0) && (
        <button
          onClick={() => navigate("/triage")}
          data-testid="today-waits-line"
          className="w-full text-left text-sm text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg px-4 py-2 flex items-center gap-2 transition-colors"
        >
          <Gavel className="h-4 w-4 text-[#174987]" />
          Waiting on <strong className="text-slate-900">{waitCounts.waiting}</strong>
          {waitCounts.chase_today > 0 && <>· <strong className="text-amber-700">{waitCounts.chase_today}</strong> to chase today</>}
          <span className="ml-auto text-xs text-[#174987]">Open triage →</span>
        </button>
      )}

      {/* 1. REVENUE SNAPSHOT — top of Today page (Feb 2026 user request) */}
      <RevenueSnapshot />

      {/* Block One item 1: "From your emails" sits directly below the Money four-states block */}
      <RexInboxTodayCards onCount={setEmailCount} />

      {/* 2. MORNING INTELLIGENCE — moved up so news is the second thing you see (Feb 2026) */}
      <div className="mb-4">
      <Sec icon={Radio} title="Morning Intelligence" count={newsItems.today.length+newsItems.previous.length+signals.length+events.length+tenders.length} open={o.intel} toggle={()=>tog("intel")} id="intelligence">
        {newsItems.today.length>0&&<Sub color="emerald">Today's Signals ({newsItems.today.length})</Sub>}
        {newsItems.today.map(n=><NewsCard key={n.news_id} n={n} onOpen={setShowArticle} onCommit={commit} onDismiss={dismissNews} onActionRoute={n => { setShowActionRoute(n); setActionRouteChoice(""); setActionRouteNote(""); }} onShare={openShare} onGive={n => openGive({ title: n.headline || n.title || "News item", subtitle: n.headline || "", source_kind: "news", source_id: n.news_id, body: n.importance || n.summary || n.description || "", source_url: n.source_url || "", source_label: "Morning Intelligence", client_name: (n.organisations||[])[0] || n.company || "", section: "commercial" })}/>)}
        {newsItems.today.length===0&&<p className="text-sm text-slate-400 py-2">No new signals today — click "Run Scrum Update" to scan</p>}
        {newsItems.previous.length>0&&<div className="flex items-center justify-between pt-3 pb-1"><span className="text-xs font-bold uppercase tracking-wider text-amber-500">Previous Signals ({newsItems.previous.length})</span><button className="text-[10px] text-slate-400 hover:text-red-500 transition-colors" data-testid="clear-old-signals-btn" onClick={async()=>{const r=await post("/mission-control/morning-news/dismiss-old",{});if(r.ok){const d=await r.json();toast.success(`Cleared ${d.total} old signals`);setNewsItems(p=>({today:p.today,previous:[]}));}else toast.error("Failed");}}>Clear all old</button></div>}
        {newsItems.previous.slice(0,15).map(n=><NewsCard key={n.news_id} n={n} onOpen={setShowArticle} onCommit={commit} onDismiss={dismissNews} onActionRoute={n => { setShowActionRoute(n); setActionRouteChoice(""); setActionRouteNote(""); }} onShare={openShare} onGive={n => openGive({ title: n.headline || n.title || "News item", subtitle: n.headline || "", source_kind: "news", source_id: n.news_id, body: n.importance || n.summary || n.description || "", source_url: n.source_url || "", source_label: "Morning Intelligence", client_name: (n.organisations||[])[0] || n.company || "", section: "commercial" })} faded/>)}
        {newsItems.previous.length>15&&<p className="text-xs text-slate-400 text-center py-1">+ {newsItems.previous.length-15} more</p>}
        {monitorCount>0&&<div className="pt-3 border-t border-slate-100"><button className="text-xs text-slate-400 hover:text-blue-500 transition-colors" onClick={async()=>{if(!showMonitor){const r=await f("/mission-control/morning-news?show_monitor=true");if(r.ok){const d=await r.json();setNewsItems({today:d.today||[],previous:d.previous||[]});setShowMonitor(true);}}else{const r=await f("/mission-control/morning-news");if(r.ok){const d=await r.json();setNewsItems({today:d.today||[],previous:d.previous||[]});setShowMonitor(false);}}}}>{showMonitor?"Hide lower priority":"Show "+monitorCount+" lower priority signals"}</button></div>}
        {(() => {
          const activeSignals = signals.filter(s => s.status !== "handed_off");
          const handedSignals = signals.filter(s => s.status === "handed_off");
          return (<>
            {activeSignals.length>0&&<Sub color="blue">Market Signals</Sub>}
            {activeSignals.map((sig,i)=><AtRiskRow key={sig.signal_id} idx={`sig-${i}`} api={API}
              item={{ type:"signal", target_type:"signal", target_id:sig.signal_id, status:sig.status,
                title:sig.title||"Market signal",
                detail:sig.summary || [sig.sector,sig.geography].filter(Boolean).join(" · "),
                owner:"", severity:"low" }}
              users={staffPool} onDone={refreshSignals} onOpen={()=>setShowArticle(sig)} />)}
            {handedSignals.length>0&&<div className="pt-3 pb-1"><span className="text-xs font-bold uppercase tracking-wider text-emerald-600" data-testid="signals-handed-header">Handed off ({handedSignals.length})</span></div>}
            {handedSignals.map((sig)=>(
              <div key={sig.signal_id} data-testid={`signal-handed-${sig.signal_id}`} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 mb-1.5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">ASSIGNED</span>
                    <span className="text-sm font-medium text-slate-500 truncate line-through decoration-slate-300">{sig.title||"Market signal"}</span>
                  </div>
                  <p className="text-xs text-emerald-700 mt-0.5" data-testid={`signal-handed-to-${sig.signal_id}`}>Assigned to {sig.handed_to_name||"a colleague"} — now in their Reviews requiring attention</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 shrink-0" data-testid={`signal-handed-history-${sig.signal_id}`} onClick={()=>openSignalHistory(sig)}>View history</Button>
              </div>
            ))}
          </>);
        })()}
        <div className="flex items-center justify-between pt-3 pb-1">
          <span className="text-xs font-bold uppercase tracking-wider text-blue-500">Events to Consider{eventsTotal>0?` (${eventsTotal})`:""}</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="text-emerald-600 h-6 text-xs px-2" data-testid="event-add-btn" onClick={()=>setShowAddEvent(true)}><Plus className="h-3.5 w-3.5 mr-0.5"/>Add Event</Button>
            <Button size="sm" variant="ghost" className="text-blue-600 h-6 text-xs px-2" data-testid="events-view-all-btn" onClick={()=>navigate("/event-radar")}>View all <ChevronRight className="h-3.5 w-3.5 ml-0.5"/></Button>
          </div>
        </div>
        {events.slice(0,6).map(e=><EventCard key={e.event_id} e={e} onOpen={setEventDetail} navigate={navigate} onPosture={setEventPosture} onDismiss={dismissEvent}/>)}
        {eventsTotal>6&&<p className="text-xs text-slate-400 text-center py-1" data-testid="events-more-count">+ {eventsTotal-6} more — <button className="text-blue-600 hover:underline" onClick={()=>navigate("/event-radar")}>open Event Radar</button></p>}
        {events.length===0&&<p className="text-sm text-slate-400 py-2" data-testid="events-empty-state">No upcoming events surfaced yet — the radar tracks African leadership, finance &amp; energy summits your team could attend. Click "Run Scrum Update" to refresh.</p>}
        <Sub color="emerald">Tenders</Sub>
        {tenders.slice(0,10).map(t=><TenderCard key={t.tender_id} t={t} onAct={act} onCommit={commit} onDismiss={dismissTender}/>)}
        {tenders.length===0&&<p className="text-sm text-slate-400 py-2" data-testid="tenders-empty-state">No live tenders surfaced yet — the radar scans procurement portals across Nigeria, Kenya, Ghana &amp; South Africa twice daily. Click "Run Scrum Update" to refresh.</p>}
      </Sec>
      </div>

      {/* 2.5 PIPELINE REPLENISHMENT — top 3 unactioned signals from past 7 days (Feb 2026) */}
      {replenishment.loaded && replenishment.items.length > 0 && (
        <div className="mb-4 rounded-xl border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 p-4" data-testid="pipeline-replenishment-section">
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-5 w-5 text-indigo-600"/>
            <h2 className="text-lg font-bold text-slate-900">Pipeline Replenishment</h2>
            <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">Top {replenishment.items.length} unactioned signals · last 7 days</span>
          </div>
          <p className="text-xs text-slate-600 mb-3">Strong signals from the past week that have not yet been routed to the pipeline. Don't let these go stale.</p>
          <div className="space-y-2">
            {replenishment.items.map((item, i) => (
              <div key={item.news_id || i} className="bg-white rounded-lg border border-indigo-200 p-3 hover:shadow-md transition-shadow" data-testid={`replenishment-item-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-bold text-slate-900">{item.headline}</span>
                      {item.company && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{item.company}</span>}
                      {item.country && <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600">{item.country}</span>}
                      <span className="text-[10px] px-2 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold">Score {item.relevance_score}</span>
                    </div>
                    {item.importance && <p className="text-xs text-slate-600 mt-1">{String(item.importance).slice(0, 200)}</p>}
                    <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-1">
                      <span>{item.analyzed_at ? new Date(item.analyzed_at).toLocaleDateString() : ""}</span>
                      {item.source_url && (
                        <a href={item.source_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline" data-testid={`replenishment-link-${i}`}>Source</a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await fetch(`${API}/mission-control/morning-news/${item.news_id}/dismiss`, {
                            method: "POST", credentials: "include",
                          });
                          setReplenishment((prev) => ({
                            ...prev,
                            items: prev.items.filter((x) => x.news_id !== item.news_id),
                          }));
                          toast.success("Signal dismissed");
                        } catch {
                          toast.error("Failed to dismiss");
                        }
                      }}
                      className="text-slate-500 hover:text-slate-900"
                      data-testid={`replenishment-dismiss-${i}`}
                      title="Dismiss this signal"
                    >
                      Dismiss
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setShowActionRoute(item); setActionRouteChoice(""); setActionRouteNote(""); }}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid={`replenishment-route-${i}`}
                    >
                      Route now
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2.6 UPCOMING OCCASIONS — compact 60-day rolling calendar (Feb 2026 redesign) */}
      {occasions.loaded && (
        <div className="mb-4 rounded-xl border border-purple-200 bg-white p-4" data-testid="upcoming-occasions-section">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Calendar className="h-5 w-5 text-purple-600"/>
            <h2 className="text-base font-bold text-slate-900">Upcoming Occasions</h2>
            <span className="text-[11px] px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold">
              {occasions.items.length} in next 60 days
            </span>
            <Button size="sm" variant="ghost" className="text-emerald-600 h-6 text-xs px-2" data-testid="occasion-add-btn" onClick={()=>setShowAddOccasion(true)}><Plus className="h-3.5 w-3.5 mr-0.5"/>Add Occasion</Button>
            <span className="text-[11px] text-slate-500 ml-auto">Create Content → drops into Backlog · Ignore → keeps it visible</span>
          </div>
          {occasions.items.length === 0 && <p className="text-xs text-slate-400" data-testid="occasions-empty">Nothing ahead that Rex has picked up. Add one it missed.</p>}
          {(() => {
            // Group by month-year for the compact calendar strip.
            const byMonth = {};
            const dedup = Array.from(new Map(occasions.items.map((o, idx) => [o.marker_id || `__idx_${idx}`, o])).values());
            for (const occ of dedup) {
              if (!occ.date) continue;
              const dt = new Date(occ.date + "T00:00:00");
              const key = dt.toLocaleDateString(undefined, { month: "long", year: "numeric" });
              (byMonth[key] = byMonth[key] || []).push({ ...occ, _dt: dt });
            }
            const months = Object.keys(byMonth);
            // REX-002 status pill colour mapping. Single word, derived from card.stage.
            const STATUS_COLOR = {
              Backlog:    "bg-slate-100   text-slate-700   border-slate-300",
              Drafting:   "bg-sky-100     text-sky-800     border-sky-300",
              Design:     "bg-indigo-100  text-indigo-800  border-indigo-300",
              Approval:   "bg-amber-100   text-amber-800   border-amber-300",
              Scheduled:  "bg-cyan-100    text-cyan-800    border-cyan-300",
              Posted:     "bg-emerald-100 text-emerald-800 border-emerald-300",
              Rejected:   "bg-rose-100    text-rose-800    border-rose-300",
              Ignored:    "bg-slate-200   text-slate-600   border-slate-300",
              Archived:   "bg-stone-200   text-stone-700   border-stone-400",
            };
            const ignoreOccasion = async (occ) => {
              const id = occ.marker_id;
              if (!id) return;
              setOccasions(s => ({
                ...s,
                items: s.items.map(x => x.marker_id === id
                  ? { ...x, dismissed: true, card_status: x.card_status || "Ignored" }
                  : x),
              }));
              try {
                const r = await fetch(`${API}/content-calendar/markers/${id}/dismiss`, { method: "POST", credentials: "include" });
                if (!r.ok) toast.error("Couldn't ignore");
                else toast.success(`Ignored ${occ.title}`);
              } catch { toast.error("Network"); }
            };
            const undoIgnore = async (occ) => {
              const id = occ.marker_id;
              if (!id) return;
              setOccasions(s => ({
                ...s,
                items: s.items.map(x => x.marker_id === id
                  ? { ...x, dismissed: false, card_status: x.linked_card_id ? x.card_status : null }
                  : x),
              }));
              try {
                const r = await fetch(`${API}/content-calendar/markers/${id}/undismiss`, { method: "POST", credentials: "include" });
                if (!r.ok) toast.error("Couldn't restore");
              } catch { toast.error("Network"); }
            };
            const createContent = async (occ) => {
              const id = occ.marker_id;
              if (!id) return;
              try {
                const r = await fetch(`${API}/content-calendar/markers/${id}/create-card`, { method: "POST", credentials: "include" });
                if (!r.ok) {
                  const e = await r.json().catch(() => ({}));
                  toast.error(e.detail || "Couldn't create card");
                  return;
                }
                const d = await r.json();
                setOccasions(s => ({
                  ...s,
                  items: s.items.map(x => x.marker_id === id
                    ? { ...x, linked_card_id: d.card_id, card_status: d.card_status || "Backlog", dismissed: false }
                    : x),
                }));
                toast.success(d.already_existed ? `Already in Backlog` : `Added to Backlog`);
              } catch { toast.error("Network"); }
            };
            return (
              <div className="space-y-3">
                {months.map(month => (
                  <div key={month}>
                    <div className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1.5 border-b border-slate-100 pb-1">{month}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                      {byMonth[month].map((occ, i) => {
                        const dayLabel = occ._dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
                        const daysAway = occ.days_away;
                        const awayLabel = daysAway === 0 ? "today" : daysAway === 1 ? "tmrw" : `${daysAway}d`;
                        const status = occ.card_status; // "Backlog" | "Drafting" | … | "Ignored" | null
                        const isIgnored = !!occ.dismissed && !occ.linked_card_id;
                        const isActioned = !!occ.linked_card_id;
                        const statusCls = STATUS_COLOR[status] || "bg-slate-100 text-slate-700 border-slate-300";
                        return (
                          <div key={occ.marker_id || i}
                            className={`group flex items-center gap-2 rounded-md border px-2.5 py-1.5 transition ${isIgnored ? "border-slate-200 bg-slate-50/60 opacity-60" : "border-slate-200 hover:border-purple-300 hover:bg-purple-50/40"}`}
                            data-testid={`occasion-row-${occ.marker_id || i}`}
                          >
                            <div className="flex-shrink-0 w-12 text-[10px] font-semibold text-purple-700 leading-tight">
                              <div>{dayLabel}</div>
                              <div className="text-slate-400 text-[9px]">{awayLabel}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div
                                className={`text-sm font-medium ${expandedOcc[occ.marker_id || i] ? "whitespace-normal break-words" : "truncate"} ${isIgnored ? "text-slate-500 line-through decoration-slate-400/70" : "text-slate-900 cursor-pointer hover:text-purple-700"}`}
                                title={occ.title}
                                onClick={() => setExpandedOcc(s => ({ ...s, [occ.marker_id || i]: !s[occ.marker_id || i] }))}
                                data-testid={`occasion-title-${occ.marker_id || i}`}
                              >{occ.title}</div>
                              <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                {occ.country && <span>{occ.country}</span>}
                                {status && (
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded border font-semibold ${statusCls}`} data-testid={`occasion-status-${occ.marker_id || i}`}>{status}</span>
                                )}
                              </div>
                            </div>
                            {/* Action buttons — Create Content / Ignore (or Undo when ignored). */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {isActioned ? (
                                <button
                                  onClick={() => navigate(`/amplify`)}
                                  className="text-[10px] px-2 py-1 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold"
                                  data-testid={`occasion-open-${occ.marker_id || i}`}
                                  title="Open in Amplify"
                                >Open</button>
                              ) : isIgnored ? (
                                <button
                                  onClick={() => undoIgnore(occ)}
                                  className="text-[10px] px-2 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 font-semibold"
                                  data-testid={`occasion-undo-${occ.marker_id || i}`}
                                  title="Undo ignore"
                                >Undo</button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => setOccasionToAssign(occ)}
                                    className="text-[10px] px-2 py-1 rounded border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 font-semibold"
                                    data-testid={`occasion-create-${occ.marker_id || i}`}
                                  >Create Content</button>
                                  <button
                                    onClick={() => ignoreOccasion(occ)}
                                    className="text-[10px] px-2 py-1 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                                    data-testid={`occasion-ignore-${occ.marker_id || i}`}
                                  >Ignore</button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* 💰 PAYMENTS DUE — overdue + next 7 days on signed deals (team review, Sep 2026) */}
      {((paymentsDue.overdue?.length||0)+(paymentsDue.upcoming?.length||0)+(paymentsDue.forecast_past?.length||0)+(paymentsDue.held_summary?.length||0))>0 && (
        <div className="mb-4 rounded-xl border-2 border-emerald-200 bg-white p-4" data-testid="payments-due-strip">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Briefcase className="h-5 w-5 text-emerald-700"/>
            <h2 className="text-lg font-bold text-slate-900">Payments</h2>
            {paymentsDue.counts?.overdue>0 && <span className="text-xs px-2 py-0.5 rounded bg-rose-100 text-rose-700 font-bold" data-testid="payments-overdue-count">{paymentsDue.counts.overdue} overdue</span>}
            {paymentsDue.counts?.due_today>0 && <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">{paymentsDue.counts.due_today} due today</span>}
            {paymentsDue.counts?.upcoming>0 && <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold">{paymentsDue.counts.upcoming} due within 7 days</span>}
            <div className="ml-auto flex items-center gap-2 text-[11px] text-slate-500">
              {Object.entries(paymentsDue.overdue_by_currency||{}).map(([cur,v])=><span key={cur} className="text-rose-700 font-semibold">{cur} {Math.round(v).toLocaleString()} overdue</span>)}
              <a href="/dashboard?tab=revenue" className="text-blue-600 underline" data-testid="payments-open-financials">Open Financials</a>
            </div>
          </div>
          {paymentsDue.held_summary?.length>0 && (
            <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2" data-testid="payments-held-summary">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-800 mb-1">On hold, not due</p>
              <div className="space-y-1">
                {paymentsDue.held_summary.map((h,i)=>{
                  const fmtCur=(by)=>Object.entries(by||{}).map(([c,v])=>`${c} ${Math.round(v).toLocaleString()}`).join(" · ")||"—";
                  return (
                    <p key={h.opportunity_id||i} className="text-xs text-slate-700" data-testid={`payments-held-item-${i}`}>
                      <span className="font-semibold text-slate-900">{h.company_name}</span>
                      {": "}{fmtCur(h.invoiced_by_currency)} invoiced, {fmtCur(h.rescheduled_by_currency)} rescheduled
                      <span className="ml-1 text-[10px] text-blue-700">({h.delivery_state_label})</span>
                    </p>
                  );
                })}
              </div>
            </div>
          )}
          {paymentsDue.counts?.forecast_past>0 && (
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="payments-forecast-past-line">
              Forecast date passed, not invoiced: <span className="font-semibold text-slate-800">{paymentsDue.counts.forecast_past} {paymentsDue.counts.forecast_past===1?"item":"items"}</span>
              {Object.entries(paymentsDue.forecast_past_by_currency||{}).map(([cur,v])=><span key={cur}>, <span className="font-semibold text-slate-800">{cur} {Math.round(v).toLocaleString()}</span></span>)}
              . These are not overdue. Invoice them to start the clock.
            </div>
          )}
          <div className="space-y-1.5">
            {[...(paymentsDue.overdue||[]), ...(paymentsDue.forecast_past||[]), ...(paymentsDue.upcoming||[])].slice(0,8).map((p,i)=>{
              const isOverdue = p.status==="Invoiced" && p.days<0;
              const isForecastPast = p.status!=="Invoiced" && p.days<0;
              return (
              <div key={p.entry_id||i} className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 border ${isOverdue?"border-rose-200 bg-rose-50/40":isForecastPast?"border-slate-200 bg-slate-50/60":p.days===0?"border-amber-200 bg-amber-50/40":"border-slate-200"}`} data-testid={`payment-due-item-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isOverdue?"bg-rose-100 text-rose-700":isForecastPast?"bg-slate-200 text-slate-600":p.days===0?"bg-amber-100 text-amber-700":"bg-slate-100 text-slate-700"}`}>{isOverdue?`OVERDUE ${-p.days}D`:isForecastPast?`FORECAST PAST ${-p.days}D`:p.days===0?"DUE TODAY":`IN ${p.days}D`}</span>
                    <span className="text-sm font-semibold text-slate-900 truncate">{p.company_name}</span>
                    <span className="text-xs text-slate-500 truncate">{p.opportunity_name}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{p.status} · {p.category||"Milestone"} · due {p.due_date}{p.owner_name?` · ${p.owner_name}`:" · no owner"}</p>
                </div>
                <span className="text-sm font-bold tabular-nums text-slate-900 shrink-0">{p.currency} {Math.round(p.amount).toLocaleString()}</span>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* S1: My work — four labelled sections, counts from the one function */}
      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="today-my-work">
        <h2 className="text-lg font-bold text-slate-900 mb-3">My work</h2>
        {/* Governing rule: every item with a headline carries its exits here. Same
            controls as the full Scrum board — the extracted BoardViews, not a rebuild. */}
        <BoardViews
          data={myBoard}
          user={user}
          onRefresh={fetchAll}
          apiBase={API}
          onOpenTask={(t)=>{setShowEditTask(t);setEditTaskForm({title:t.title||"",assigned_to_name:t.assigned_to_name||t.owner_name||t.owner||"",status:t.status||"",description:t.description||""});}}
        />
        {(() => {
          const me = user?.user_id || "";
          const mine = (commits?.actions || []).filter(a => (a.owner_id || "") === me);
          return (
            <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50" data-testid="today-reviews">
              <div className="px-3 py-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-indigo-800">Reviews requiring attention</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-indigo-200 text-indigo-800" data-testid="today-reviews-count">{mine.length}</span>
              </div>
              {mine.length > 0 && (
                <div className="divide-y divide-indigo-100 border-t border-indigo-100 max-h-[28rem] overflow-y-auto">
                  {mine.map(a => {
                    const isBlocked = a.status === "Blocked";
                    const isResolved = a.status === "Resolved" || a.review_resolved;
                    const noReason = !a.blocker_reason || a.blocker_reason === "Blocked";
                    const ui = commitUI && commitUI.id === a.action_id ? commitUI : null;
                    return (
                      <div key={a.action_id} className="bg-white px-3 py-2.5" data-testid={`commitment-card-${a.action_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            {(a.linked_client_id || a.source_url || a.linked_client_name || commitmentInternalPath(a)) ? (
                              <button type="button" onClick={() => openCommitment(a)} data-testid={`commitment-open-${a.action_id}`} title={a.linked_client_id ? "Open client workspace" : a.source_url ? "Open source" : commitmentInternalPath(a) ? "Open in Rex" : "Link a client to act on this"} className="group text-left text-sm font-medium text-slate-900 hover:text-indigo-700 inline-flex items-start gap-1">
                                <span className="min-w-0 group-hover:underline">{a.title}</span>
                                <ExternalLink className="h-3.5 w-3.5 mt-0.5 shrink-0 text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </button>
                            ) : (
                              <div className="text-sm font-medium text-slate-900">{a.title}</div>
                            )}
                            <div className="mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-1 text-xs">
                              <span className="text-slate-400">{a.due_date || "—"}</span>
                              {/* Client: open if linked, else an explicit unlinked prompt (never a dead name) */}
                              {a.linked_client_id ? (
                                <button className="text-indigo-700 underline hover:text-indigo-900" data-testid={`commitment-open-client-${a.action_id}`} onClick={() => openClient(a.linked_client_id)}>
                                  {a.linked_client_name || "Open client"}
                                </button>
                              ) : a.linked_client_name ? (
                                <button className="text-amber-700 underline decoration-dotted hover:text-amber-900" data-testid={`commitment-link-client-${a.action_id}`} onClick={() => resolveCommitClient(a)}>
                                  Link client: {a.linked_client_name}
                                </button>
                              ) : null}
                              {isBlocked && (noReason ? (
                                <button className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5" data-testid={`commitment-blocked-noreason-${a.action_id}`} onClick={() => setCommitUI({ id: a.action_id, mode: "blockreason", value: "" })}>
                                  Blocked — no reason given · add one
                                </button>
                              ) : (
                                <span className="text-rose-700" data-testid={`commitment-blocked-reason-${a.action_id}`}>Blocked: {a.blocker_reason}</span>
                              ))}
                              {isResolved && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid={`commitment-disposition-${a.action_id}`}>
                                  {a.review_outcome_label || "Resolved"}
                                </span>
                              )}
                            </div>
                            {(a.source_url || a.source_kind || a.description) && (
                              <div className="mt-1.5 text-xs">
                                {a.handed_from_name && (
                                  <div className="text-[11px] text-slate-500" data-testid={`commitment-handed-from-${a.action_id}`}>Handed over by {a.handed_from_name}</div>
                                )}
                                {a.source_url ? (
                                  <a href={a.source_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid={`commitment-source-link-${a.action_id}`}>
                                    <ExternalLink className="h-3 w-3" /> {a.source_label || "Source"}{a.source_kind ? ` · ${a.source_kind}` : ""}
                                  </a>
                                ) : a.source_label || a.source_kind ? (
                                  <span className="text-slate-500" data-testid={`commitment-source-${a.action_id}`}>From {a.source_label || a.source_kind}</span>
                                ) : null}
                                {a.description && (
                                  <p className="mt-1 text-slate-600 whitespace-pre-wrap max-h-28 overflow-y-auto bg-slate-50 border border-slate-100 rounded px-2 py-1" data-testid={`commitment-body-${a.action_id}`}>{a.description}</p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {isResolved ? (
                              <button className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`commitment-acknowledge-${a.action_id}`} onClick={() => commitUpdate(a.action_id, { status: "Completed" }, "Acknowledged — removed from your list")}>Got it</button>
                            ) : (
                              <>
                                <button className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`commitment-done-${a.action_id}`} onClick={() => commitUpdate(a.action_id, { status: "Completed" }, "Done")}>Done</button>
                                <button className="text-[11px] px-2 py-1 rounded bg-slate-200 text-slate-700 hover:bg-slate-300" data-testid={`commitment-notdoing-${a.action_id}`} onClick={() => setCommitUI({ id: a.action_id, mode: "notdoing", value: "" })}>Not doing</button>
                                <button className="text-[11px] px-2 py-1 rounded bg-slate-200 text-slate-700 hover:bg-slate-300" data-testid={`commitment-reassign-${a.action_id}`} onClick={() => setCommitUI({ id: a.action_id, mode: "reassign", toId: "" })}>Reassign</button>
                              </>
                            )}
                          </div>
                        </div>

                        {ui && ui.mode === "notdoing" && (
                          <div className="mt-2 flex items-center gap-2" data-testid={`commitment-notdoing-panel-${a.action_id}`}>
                            <input className="flex-1 text-xs border border-slate-300 rounded px-2 py-1" placeholder="Why not? (required, your own words)" value={ui.value} data-testid={`commitment-notdoing-input-${a.action_id}`} onChange={e => setCommitUI({ ...ui, value: e.target.value })} />
                            <button className="text-[11px] px-2 py-1 rounded bg-slate-800 text-white disabled:opacity-40" disabled={!ui.value.trim()} data-testid={`commitment-notdoing-apply-${a.action_id}`} onClick={() => commitUpdate(a.action_id, { status: "Not Doing", not_doing_reason: ui.value.trim() }, "Marked not doing")}>Apply</button>
                            <button className="text-[11px] px-2 py-1 text-slate-500" onClick={() => setCommitUI(null)}>Cancel</button>
                          </div>
                        )}
                        {ui && ui.mode === "blockreason" && (
                          <div className="mt-2 flex items-center gap-2" data-testid={`commitment-blockreason-panel-${a.action_id}`}>
                            <input className="flex-1 text-xs border border-slate-300 rounded px-2 py-1" placeholder="What is blocking this? (a real reason)" value={ui.value} data-testid={`commitment-blockreason-input-${a.action_id}`} onChange={e => setCommitUI({ ...ui, value: e.target.value })} />
                            <button className="text-[11px] px-2 py-1 rounded bg-slate-800 text-white disabled:opacity-40" disabled={!ui.value.trim()} data-testid={`commitment-blockreason-apply-${a.action_id}`} onClick={() => commitUpdate(a.action_id, { status: "Blocked", blocker_reason: ui.value.trim() }, "Reason recorded")}>Save reason</button>
                            <button className="text-[11px] px-2 py-1 text-slate-500" onClick={() => setCommitUI(null)}>Cancel</button>
                          </div>
                        )}
                        {ui && ui.mode === "reassign" && (
                          <div className="mt-2 flex items-center gap-2" data-testid={`commitment-reassign-panel-${a.action_id}`}>
                            <select className="flex-1 text-xs border border-slate-300 rounded px-2 py-1" value={ui.toId} data-testid={`commitment-reassign-select-${a.action_id}`} onChange={e => setCommitUI({ ...ui, toId: e.target.value })}>
                              <option value="">Choose a person…</option>
                              {commitUsers.filter(u => (u.user_id || u.id) !== me).map(u => (
                                <option key={u.user_id || u.id} value={u.user_id || u.id}>{u.full_name || u.name || u.email}</option>
                              ))}
                            </select>
                            <button className="text-[11px] px-2 py-1 rounded bg-slate-800 text-white disabled:opacity-40" disabled={!ui.toId} data-testid={`commitment-reassign-apply-${a.action_id}`} onClick={() => { const u = commitUsers.find(x => (x.user_id || x.id) === ui.toId); commitUpdate(a.action_id, { status: "Reassigned", reassign_to_id: ui.toId, reassign_to_name: u ? (u.full_name || u.name || u.email) : "" }, "Reassigned"); }}>Hand over</button>
                            <button className="text-[11px] px-2 py-1 text-slate-500" onClick={() => setCommitUI(null)}>Cancel</button>
                          </div>
                        )}
                        {ui && ui.mode === "disambig" && (
                          <div className="mt-2 text-xs" data-testid={`commitment-disambig-panel-${a.action_id}`}>
                            {ui.candidates.length === 0 ? (
                              <span className="text-slate-600">No client matches “{a.linked_client_name}”. Which client is this about? Set it on the client record, then re-link.</span>
                            ) : (
                              <div className="flex items-center flex-wrap gap-1.5">
                                <span className="text-slate-600">“{a.linked_client_name}” matches several — which one?</span>
                                {ui.candidates.map(c => (
                                  <button key={c.client_id} className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 hover:bg-indigo-200" data-testid={`commitment-disambig-pick-${a.action_id}-${c.client_id}`} onClick={() => resolveCommitClient(a, c.client_id)}>{c.name}</button>
                                ))}
                              </div>
                            )}
                            <button className="ml-2 text-slate-500" onClick={() => setCommitUI(null)}>Dismiss</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </div>


      {/* ⚠️ AT RISK STRIP — always visible, prevents dropped balls */}
      {atRisk.items?.length>0 && (
        <div className="mb-4 rounded-xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4" data-testid="at-risk-strip">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-600"/>
            <h2 className="text-lg font-bold text-slate-900">At Risk</h2>
            <button className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-bold hover:bg-amber-200 transition-colors" data-testid="at-risk-open-all" onClick={() => openAtRiskQueue("all", "All at-risk items")}>{atRisk.total_items} items · showing {atRisk.items.length} → view all</button>
            <div className="ml-auto flex gap-1 text-[10px] text-slate-500">
              {atRisk.counts?.overdue_task>0&&<button className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors" data-testid="at-risk-count-overdue_task" onClick={()=>openAtRiskQueue("overdue_task","Overdue tasks")}>{atRisk.counts.overdue_task} overdue</button>}
              {atRisk.counts?.stalled_with_client>0&&<button className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors" data-testid="at-risk-count-stalled_with_client" onClick={()=>openAtRiskQueue("stalled_with_client","Stalled with client")}>{atRisk.counts.stalled_with_client} stalled with client</button>}
              {atRisk.counts?.stalled_deal>0&&<button className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors" data-testid="at-risk-count-stalled_deal" onClick={()=>openAtRiskQueue("stalled_deal","Stalled deals")}>{atRisk.counts.stalled_deal} stalled</button>}
              {atRisk.counts?.no_contact>0&&<button className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors" data-testid="at-risk-count-no_contact" onClick={()=>openAtRiskQueue("no_contact","Going silent")}>{atRisk.counts.no_contact} silent</button>}
              {atRisk.counts?.pending_approval>0&&<button className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors" data-testid="at-risk-count-pending_approval" onClick={()=>openAtRiskQueue("pending_approval","Pending approvals")}>{atRisk.counts.pending_approval} approvals</button>}
              {atRisk.counts?.content_approval>0&&<button className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors" data-testid="at-risk-count-content_approval" onClick={()=>openAtRiskQueue("content_approval","Content review")}>{atRisk.counts.content_approval} content review</button>}
              {atRisk.counts?.content_overdue>0&&<button className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors" data-testid="at-risk-count-content_overdue" onClick={()=>openAtRiskQueue("content_overdue","Content overdue")}>{atRisk.counts.content_overdue} content overdue</button>}
            </div>
          </div>
          <div className="space-y-1.5">
            {atRisk.items.map((r,i)=>(
              <AtRiskRow
                key={i}
                item={r}
                idx={i}
                api={API}
                users={commitUsers}
                onDone={fetchAll}
                onOpenClient={(cid) => openClient(cid)}
                onOpenBoard={() => navigate(`/scrum`)}
                onOpen={openAtRiskItem}
                onChase={openChaseDialog}
              />
            ))}
          </div>
        </div>
      )}

      {/* 🔭 LENS — analyst queue tile (Phase 3 of News Triage Overhaul) */}
      {(() => {
        const counts = lensTile.by_lane || {};
        const total = Object.values(counts).reduce((a,b)=>a+(b||0),0);
        const breached = lensTile.breached || 0;
        if (total === 0 && breached === 0) return null;
        const lanes = [
          { id:"triage",             label:"Triage",      color:"bg-slate-100 text-slate-800" },
          { id:"researching",        label:"Researching", color:"bg-sky-100 text-sky-900" },
          { id:"research_completed", label:"Researched",  color:"bg-cyan-100 text-cyan-900" },
          { id:"drafting_brief",     label:"Drafting",    color:"bg-indigo-100 text-indigo-900" },
          { id:"review",             label:"Review",      color:"bg-amber-100 text-amber-900" },
          { id:"approved",           label:"Approved",    color:"bg-emerald-100 text-emerald-900" },
          { id:"rejected",           label:"Rejected",    color:"bg-rose-100 text-rose-900" },
          { id:"completed",          label:"Completed",   color:"bg-stone-100 text-stone-800" },
        ];
        const tone = breached >= 3 ? "border-rose-300 bg-gradient-to-r from-rose-50/60 to-amber-50/30"
                   : breached >= 1 ? "border-amber-300 bg-gradient-to-r from-amber-50/60 to-indigo-50/30"
                   : "border-indigo-200 bg-gradient-to-r from-indigo-50/50 to-sky-50/30";
        return (
          <div className={`mb-4 rounded-xl border-2 p-4 ${tone}`} data-testid="lens-tile">
            <div className="flex items-center gap-2 mb-3">
              <Telescope className="h-5 w-5 text-indigo-600"/>
              <h2 className="text-lg font-bold text-slate-900">Lens — Analyst Queue</h2>
              <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">{total} active</span>
              {breached > 0 && (
                <span className="text-xs px-2 py-0.5 rounded bg-rose-600 text-white font-bold" data-testid="lens-tile-breached">
                  {breached} SLA breached
                </span>
              )}
              <button className="ml-auto text-xs text-indigo-600 hover:underline flex items-center gap-1"
                onClick={()=>navigate("/lens")} data-testid="lens-tile-open-board">
                Open board →
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {lanes.map(l => (
                <button key={l.id}
                  data-testid={`lens-tile-lane-${l.id}`}
                  onClick={()=>navigate("/lens")}
                  className="rounded-lg border border-white/60 bg-white/80 hover:bg-white p-2.5 text-left transition">
                  <div className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded inline-block mb-1 ${l.color}`}>{l.label}</div>
                  <p className="text-xl font-bold text-slate-900 leading-none">{counts[l.id] || 0}</p>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* 🎨 DESIGNER HEATMAP — backlog-load per designer (cards in IN_DESIGN/IN_REVIEW + overdue) */}
      {designerHeatmap.designers?.length>0 && (
        <div className="mb-4 rounded-xl border-2 border-indigo-200 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 p-4" data-testid="designer-heatmap-strip">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="h-5 w-5 text-indigo-600"/>
            <h2 className="text-lg font-bold text-slate-900">Designer Heatmap</h2>
            <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">{designerHeatmap.totals?.total_active||0} active · {designerHeatmap.totals?.total_overdue||0} overdue</span>
            <div className="ml-auto flex items-center gap-3 text-[10px] text-slate-500">
              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-emerald-500"/>≤2 active</span>
              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-amber-500"/>3-5 active</span>
              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-red-500"/>≥6 or overdue</span>
              <button className="ml-2 text-indigo-600 hover:underline" onClick={()=>navigate("/amplify")} data-testid="heatmap-open-board-btn">Open Board →</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
            {designerHeatmap.designers.map((d,i)=>{
              const colors = d.status==="red" ? "border-red-300 bg-red-50/70" : d.status==="amber" ? "border-amber-300 bg-amber-50/70" : "border-emerald-300 bg-emerald-50/70";
              const dot = d.status==="red" ? "bg-red-500" : d.status==="amber" ? "bg-amber-500" : "bg-emerald-500";
              return (
                <button key={i} className={`text-left rounded-lg border-2 ${colors} p-2.5 hover:shadow-md transition`} data-testid={`heatmap-designer-${i}`} onClick={()=>setHeatmapDetail(d)}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${dot}`}/>
                    <span className="text-sm font-bold text-slate-900 truncate">{d.designer_name||"Unassigned"}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-slate-700">
                    <span className="font-semibold"><span className="text-base text-slate-900 font-bold">{d.active_load}</span> active</span>
                    {d.overdue>0 && <span className="font-semibold text-red-700"><span className="text-base font-bold">{d.overdue}</span> overdue</span>}
                  </div>
                  {d.throughput_7d>0 && <p className="text-[10px] text-slate-500 mt-0.5">{d.throughput_7d} shipped (7d)</p>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* SCRUM BOARD — full filter/search/bulk view (distinct from Execution Board triage). Placed right after Designer Heatmap so everyone's to-do list is visible on Today (Sep 2026 Release 1). */}
      <Sec icon={Users} title="Scrum Board (Full Task Management)" count={scrumTasks.length} open={o.scrum} toggle={()=>tog("scrum")} id="scrum">
        <ScrumManagement user={user} embedded />
      </Sec>

      {/* 📌 TODAY'S EXECUTION BOARD — all tasks, 4 mandatory lanes */}
      <div className="mb-4 rounded-xl border-2 border-slate-200 bg-white p-4" data-testid="execution-board">
        <div className="flex items-center gap-2 mb-3">
          <Briefcase className="h-5 w-5 text-slate-700"/>
          <h2 className="text-lg font-bold text-slate-900">Today's Execution Board</h2>
          <span className="text-xs text-slate-500">{execBoard.total||0} open tasks</span>
          {execBoard.orphan_count>0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700" title="Tasks with no client / category / move link">{execBoard.orphan_count} unlinked</span>}
          <Button size="sm" variant="outline" className="ml-auto" data-testid="new-task-btn" onClick={()=>{setShowNewTask(true);setNewTaskForm({title:"",description:"",category:"",client_id:"",client_name:"",assigned_to_name:user?.name||"",due_date:new Date(Date.now()+86400000).toISOString().slice(0,10),priority:"medium"});}}><Plus className="h-4 w-4 mr-1"/>New Task</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            {key:"COMMERCIAL",label:"🎯 Commercial",color:"border-emerald-200 bg-emerald-50/30",badge:"bg-emerald-100 text-emerald-700"},
            {key:"CLIENT_DELIVERY",label:"🤝 Client Delivery",color:"border-blue-200 bg-blue-50/30",badge:"bg-blue-100 text-blue-700"},
            {key:"ADMIN_FINANCE",label:"📋 Admin / Finance",color:"border-slate-200 bg-slate-50/50",badge:"bg-slate-200 text-slate-700"},
            {key:"INTERNAL",label:"🏠 Internal",color:"border-purple-200 bg-purple-50/30",badge:"bg-purple-100 text-purple-700"},
          ].map(lane=>{
            const tasks = execBoard.lanes?.[lane.key] || [];
            const open = execLaneOpen[lane.key];
            return (
              <div key={lane.key} className={`rounded-lg border-2 ${lane.color}`} data-testid={`exec-lane-${lane.key}`}>
                <button className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50/50 transition" onClick={()=>setExecLaneOpen(p=>({...p,[lane.key]:!p[lane.key]}))}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-800">{lane.label}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${lane.badge}`}>{tasks.length}</span>
                  </div>
                  {open?<ChevronUp className="h-4 w-4 text-slate-400"/>:<ChevronDown className="h-4 w-4 text-slate-400"/>}
                </button>
                {open && (
                  <div className="px-3 pb-3 space-y-1.5 border-t border-slate-100 pt-2">
                    {tasks.length===0 ? <p className="text-xs text-slate-400 text-center py-2">No tasks</p> : tasks.slice(0,10).map(t=>(
                      <div key={t.task_id} className={`bg-white rounded px-2.5 py-2 border ${t.overdue?"border-red-200":"border-slate-100"} flex items-start justify-between gap-2`} data-testid={`exec-task-${t.task_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${t.status==="In Progress"?"bg-blue-100 text-blue-700":t.status==="Blocked"?"bg-red-100 text-red-700":t.status==="On Hold"?"bg-amber-100 text-amber-700":"bg-slate-100 text-slate-600"}`}>{t.status||"—"}</span>
                            {t.overdue && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">OVERDUE</span>}
                            {t.is_orphan && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" title="No client / category / move link">UNLINKED</span>}
                          </div>
                          <p className="text-sm text-slate-900 mt-0.5 leading-snug">{t.title}</p>
                          <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-0.5">
                            {t.owner ? <span>{t.owner}</span> : <span className="text-amber-600 font-semibold">no owner</span>}
                            {t.due_date ? <span>{fmtDate(t.due_date)}</span> : <span className="text-amber-600 font-semibold">no due date</span>}
                            {t.client_name && <span className="text-blue-600">{t.client_name}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {t.is_orphan && <button className="text-[10px] text-amber-700 hover:underline" data-testid={`link-task-${t.task_id}`} onClick={()=>{setShowLinkTask(t);setLinkForm({category:t.category||"",client_id:"",client_name:""});}}>Link</button>}
                          <button className="text-[10px] text-blue-600 hover:underline" onClick={()=>{setShowEditTask(t);setEditTaskForm({title:t.title||"",assigned_to_name:t.owner||"",status:t.status||"",description:t.description||""});}}>Edit</button>
                          <button className="text-[10px] text-green-700 hover:underline" data-testid={`exec-done-${t.task_id}`} onClick={()=>completeTask(t)}>Done</button>
                        </div>
                      </div>
                    ))}
                    {tasks.length>10 && <p className="text-[10px] text-slate-400 text-center">+ {tasks.length-10} more</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* WEEKLY BD PLAYBOOK — Monday strategist */}
      {weeklyPlaybook && (weeklyPlaybook.counts?.priority_moves>0 || weeklyPlaybook.counts?.account_alerts>0 || weeklyPlaybook.counts?.dormant_reactivations>0 || weeklyPlaybook.counts?.pipeline_risks>0) && (
        <div className="mb-4 rounded-xl border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 p-4" data-testid="weekly-playbook-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-indigo-600"/>
              <h2 className="text-lg font-bold text-slate-900">Weekly BD Playbook</h2>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">Week of {weeklyPlaybook.week_of}</span>
            </div>
            <Button size="sm" variant="outline" data-testid="toggle-playbook-btn" onClick={()=>setShowPlaybook(s=>!s)}>
              {showPlaybook ? <>Hide <ChevronUp className="h-4 w-4 ml-1"/></> : <>Open <ChevronDown className="h-4 w-4 ml-1"/></>}
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div className="bg-white rounded px-2 py-1 border border-indigo-100"><span className="font-bold text-indigo-700">{weeklyPlaybook.counts?.priority_moves||0}</span> Priority Moves</div>
            <div className="bg-white rounded px-2 py-1 border border-red-100"><span className="font-bold text-red-600">{weeklyPlaybook.counts?.account_alerts||0}</span> Account Alerts</div>
            <div className="bg-white rounded px-2 py-1 border border-amber-100"><span className="font-bold text-amber-600">{weeklyPlaybook.counts?.dormant_reactivations||0}</span> Dormant Reactivations</div>
            <div className="bg-white rounded px-2 py-1 border border-orange-100"><span className="font-bold text-orange-600">{weeklyPlaybook.counts?.pipeline_risks||0}</span> Pipeline Risks</div>
            <div className="bg-white rounded px-2 py-1 border border-purple-100"><span className="font-bold text-purple-600">{weeklyPlaybook.counts?.content_direction||0}</span> Content Themes</div>
          </div>
          {showPlaybook && (
            <div className="mt-4 space-y-4">
              {weeklyPlaybook.sections?.account_alerts?.length>0 && (
                <div>
                  <h3 className="text-sm font-bold text-red-700 mb-2">⚠️ Account Alerts</h3>
                  <div className="space-y-2">
                    {weeklyPlaybook.sections.account_alerts.map((a,i)=>(
                      <div key={i} className={`bg-white rounded-lg border border-red-200 p-3 ${a.client_id?"cursor-pointer hover:border-red-400 hover:shadow-sm transition":""}`} data-testid={`playbook-alert-${i}`}
                        onClick={a.client_id?(()=>navigate(`/clients/${a.client_id}`)):undefined}
                        role={a.client_id?"link":undefined}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-sm font-bold text-slate-900">{a.entity} <span className="text-xs text-red-600">({a.signal_count} signals / 30d)</span></p>
                            <p className="text-xs text-slate-500 mt-0.5">{a.sample_headline}</p>
                            <p className="text-xs text-red-700 mt-1">{a.recommended_action}</p>
                          </div>
                          <PlaybookDealControls item={{ ...a, company_name: a.entity }} prefix={`playbook-alert-${i}`} navigate={navigate}
                            onChooseDeal={(it)=>setChooseDeal({ company_name: a.entity, client_id: a.client_id, open_opps: a.open_opps||[] })}
                            onFindClient={()=>openClientMatch(a.entity)} />
                        </div>
                        {a.opportunity_id && (
                          <div className="mt-2 pt-2 border-t border-red-100" onClick={(e)=>e.stopPropagation()}>
                            <DealStatusControl dealId={a.opportunity_id} currentStage={a.stage} canAct={canEditDeals}
                              testidPrefix={`playbook-alert-status-${i}`} compact onChanged={()=>fetchAll&&fetchAll()} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {weeklyPlaybook.sections?.dormant_reactivations?.length>0 && (
                <div>
                  <h3 className="text-sm font-bold text-amber-700 mb-2">💤 Dormant Reactivations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {weeklyPlaybook.sections.dormant_reactivations.map((d,i)=>(
                      <div key={i} className="bg-white rounded-lg border border-amber-200 p-2.5" data-testid={`playbook-dormant-${i}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">{d.company_name}</p>
                            <p className="text-xs text-slate-500">{d.sector} · {d.days_since}d · Score {d.reactivation_score}</p>
                            <p className="text-xs text-amber-700 mt-0.5 line-clamp-2">{d.reason}</p>
                          </div>
                          {d.client_id && <Button size="sm" variant="ghost" className="h-7" onClick={()=>navigate(`/clients/${d.client_id}`)}>→</Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {weeklyPlaybook.sections?.pipeline_risks?.length>0 && (
                <div>
                  <h3 className="text-sm font-bold text-orange-700 mb-2">🛡️ Pipeline Risks</h3>
                  <div className="space-y-1.5">
                    {weeklyPlaybook.sections.pipeline_risks.map((r,i)=>(
                      <div key={i} className="bg-white rounded-lg border border-orange-200 p-2.5" data-testid={`playbook-risk-${i}`}>
                        <div className={`flex items-start justify-between gap-2 ${r.client_id?"cursor-pointer":""}`}
                          onClick={r.client_id?(()=>navigate(`/clients/${r.client_id}`)):undefined}
                          role={r.client_id?"link":undefined}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900">{r.company_name} <span className="text-xs text-orange-600">({r.stage})</span></p>
                            <p className="text-xs text-slate-500">{r.risk_reason} · Owner: {r.owner||"unassigned"}</p>
                            <p className="text-xs text-orange-700 mt-0.5">{r.recommended_action}</p>
                          </div>
                          <PlaybookDealControls item={r} prefix={`playbook-risk-${i}`} navigate={navigate}
                            onChooseDeal={(it)=>setChooseDeal({ company_name: r.company_name, client_id: r.client_id, open_opps: r.open_opps||[] })} />
                        </div>
                        {r.opportunity_id && (
                          <div className="mt-2 pt-2 border-t border-orange-100" onClick={(e)=>e.stopPropagation()}>
                            <DealStatusControl dealId={r.opportunity_id} currentStage={r.stage} canAct={canEditDeals}
                              testidPrefix={`playbook-risk-status-${i}`} compact onChanged={()=>fetchAll&&fetchAll()} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {weeklyPlaybook.sections?.content_direction?.length>0 && (
                <div>
                  <h3 className="text-sm font-bold text-purple-700 mb-2">📢 Content Direction</h3>
                  <div className="space-y-1.5">
                    {weeklyPlaybook.sections.content_direction.map((c,i)=>{
                      const moved = !!c.assigned_to_name;
                      return (
                      <div key={i} className={`bg-white rounded-lg border p-2.5 flex items-start justify-between gap-2 cursor-pointer hover:shadow-sm transition ${moved?"border-emerald-200 hover:border-emerald-400":"border-purple-200 hover:border-purple-400"}`} data-testid={`playbook-content-${i}`} onClick={()=>moved ? navigate(c.linked_draft_id?`/content-studio?draft=${c.linked_draft_id}`:"/content-studio") : openThemeWork(c)} role="button">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-purple-900">{c.theme} <span className="text-xs text-slate-500">({c.signal_count} signals)</span></p>
                          <p className="text-xs text-purple-700 mt-0.5">{c.suggested_angle}</p>
                          {moved && (
                            <p className="text-xs text-emerald-700 mt-1 font-medium" data-testid={`playbook-content-moved-${i}`}>Assigned to {c.assigned_to_name} — drafting in Content Studio, tracked in their scrum</p>
                          )}
                        </div>
                        {moved ? (
                          <Button size="sm" variant="outline" className="h-7 shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`playbook-content-open-${i}`} onClick={(e)=>{e.stopPropagation(); navigate(c.linked_draft_id?`/content-studio?draft=${c.linked_draft_id}`:"/content-studio");}}>Open draft</Button>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 shrink-0 border-purple-300 text-purple-700 hover:bg-purple-50" data-testid={`playbook-content-work-${i}`} onClick={(e)=>{e.stopPropagation(); openThemeWork(c);}}>Work this theme</Button>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* MORNING INTELLIGENCE moved above — see top of page (Feb 2026 reorder) */}

      {/* Upcoming Events now surfaced in the Morning Intelligence section, just before Tenders (Jun 2026 user request) */}

      {/* SCRUM BOARD moved up — now renders directly after Designer Heatmap / before Execution Board (Sep 2026 Release 1) */}

      {/* ═══ DIALOGS ═══ */}
      {/* Where should this go? — Rex refuses to auto-create clients on an unclear match */}
      <Dialog open={!!pipelineAsk} onOpenChange={(v)=>{ if(!v) setPipelineAsk(null); }}>
        <DialogContent className="max-w-md" data-testid="pipeline-assign-dialog">
          <DialogHeader><DialogTitle>Where should this go?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Rex could not confidently match <strong>{pipelineAsk?.company}</strong> to an existing client, so nothing was created. Pick the right client, track it without adding anything to the client book, or — only if this really is a client of ours — create a new record.</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {(pipelineAsk?.candidates||[]).length===0 && <span className="text-xs text-slate-400">No close matches found.</span>}
            {(pipelineAsk?.candidates||[]).map(c=>(
              <Button key={c.client_id} size="sm" variant="outline" data-testid={`pipeline-assign-${c.client_id}`} onClick={async()=>{
                const r = await post("/mission-control/signal-to-pipeline", {...pipelineAsk.payload, client_id: c.client_id});
                if(r.ok){ toast.success(`Pipeline opportunity created under ${c.company_name}`); } else { toast.error("Failed to create pipeline entry"); }
                setPipelineAsk(null);
              }}>{c.company_name}</Button>
            ))}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={()=>setPipelineAsk(null)} data-testid="pipeline-assign-cancel">Cancel</Button>
            <Button variant="outline" data-testid="pipeline-assign-track" onClick={async()=>{
              const r = await post("/prospects", { company_name: pipelineAsk.company, headline: pipelineAsk.headline, source: "morning_intelligence_signal", source_signal_headline: pipelineAsk.headline, source_signal_url: pipelineAsk.payload?.source_url || "" });
              if(r.ok){ toast.success(`Tracking ${pipelineAsk.company} as a prospect — nothing added to the client book`); } else { toast.error("Failed to track"); }
              setPipelineAsk(null);
            }}>Not a client — just watch it</Button>
            <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="pipeline-assign-new" onClick={async()=>{
              const r = await post("/mission-control/signal-to-pipeline", {...pipelineAsk.payload, create_client: true});
              if(r.ok){ toast.success(`New client ${pipelineAsk.company} created with pipeline opportunity`); } else { toast.error("Failed to create pipeline entry"); }
              setPipelineAsk(null);
            }}>It's a new client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* N3 Commit B — find/create a client for an account alert that matched no client */}
      <Dialog open={!!clientMatch} onOpenChange={(v)=>{ if(!v) setClientMatch(null); }}>
        <DialogContent className="max-w-md" data-testid="playbook-client-match-dialog">
          <DialogHeader><DialogTitle>Find or create a client</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This alert for <strong>{clientMatch?.entity}</strong> is not linked to any client record. Open an existing client if one matches, or watch it as a prospect — nothing is added to the client book unless you choose to.</p>
          <div className="space-y-1.5 mt-2 max-h-60 overflow-y-auto" data-testid="playbook-client-match-results">
            {matchLoading && <span className="text-xs text-slate-400">Searching…</span>}
            {!matchLoading && matchResults.length===0 && <span className="text-xs text-slate-400">No matching client found.</span>}
            {matchResults.map(c=>(
              <div key={c.client_id} className="flex items-center justify-between gap-2 border rounded-lg px-2.5 py-1.5">
                <div className="min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{c.company_name}</p>{c.sector && <p className="text-[10px] text-slate-400">{c.sector}</p>}</div>
                <Button size="sm" variant="outline" className="h-7 shrink-0" data-testid={`playbook-client-match-open-${c.client_id}`} onClick={()=>{ setClientMatch(null); navigate(`/clients/${c.client_id}`); }}>Open client</Button>
              </div>
            ))}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={()=>setClientMatch(null)} data-testid="playbook-client-match-cancel">Cancel</Button>
            <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="playbook-client-match-track" onClick={()=>trackProspect(clientMatch.entity)}>Watch as prospect</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* N4 Commit A — several open deals: name how many and let the user choose which to open */}
      <Dialog open={!!chooseDeal} onOpenChange={(v)=>{ if(!v) setChooseDeal(null); }}>
        <DialogContent className="max-w-md" data-testid="playbook-choose-deal-dialog">
          <DialogHeader><DialogTitle>Which deal?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600"><strong>{chooseDeal?.company_name}</strong> has {(chooseDeal?.open_opps||[]).length} open deals. Choose the one to open.</p>
          <div className="space-y-1.5 mt-2 max-h-64 overflow-y-auto" data-testid="playbook-choose-deal-list">
            {(chooseDeal?.open_opps||[]).map(o=>(
              <div key={o.opportunity_id} className="flex items-center justify-between gap-2 border rounded-lg px-2.5 py-1.5">
                <div className="min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{o.opportunity_name}</p>{o.stage && <p className="text-[10px] text-slate-400">{o.stage}</p>}</div>
                <Button size="sm" variant="outline" className="h-7 shrink-0" data-testid={`playbook-choose-deal-${o.opportunity_id}`} onClick={()=>{ const cid=chooseDeal.client_id; setChooseDeal(null); navigate(`/clients/${cid}?tab=opportunities`); }}>Open</Button>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setChooseDeal(null)} data-testid="playbook-choose-deal-cancel">Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!themeWork} onOpenChange={v=>{if(!v)setThemeWork(null);}}>
        <DialogContent className="max-w-lg" data-testid="theme-work-dialog">
          <DialogHeader><DialogTitle>Work this content theme</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-base font-bold text-purple-900" data-testid="theme-work-title">{themeWork?.theme}</p>
              <p className="text-sm text-slate-600 mt-0.5">{themeWork?.suggested_angle}</p>
            </div>
            {(themeWork?.sample_headlines||[]).length>0 && (
              <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Signals behind this theme</p>
                <ul className="space-y-0.5">
                  {themeWork.sample_headlines.map((h,hi)=><li key={hi} className="text-xs text-slate-600 truncate">• {h}</li>)}
                </ul>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Voice</p>
              <div className="flex gap-2">
                <button type="button" onClick={()=>setThemePostType("company")} data-testid="theme-posttype-company"
                  className={`flex-1 text-xs rounded-lg border px-2 py-1.5 transition-colors ${themePostType==="company"?"border-purple-500 bg-purple-50 text-purple-700 font-semibold":"border-slate-200 text-slate-500 hover:border-slate-300"}`}>RESILIGENT (Company)</button>
                <button type="button" onClick={()=>setThemePostType("personal")} data-testid="theme-posttype-personal"
                  className={`flex-1 text-xs rounded-lg border px-2 py-1.5 transition-colors ${themePostType==="personal"?"border-purple-500 bg-purple-50 text-purple-700 font-semibold":"border-slate-200 text-slate-500 hover:border-slate-300"}`}>Remi (Personal)</button>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Assign to</p>
              <select data-testid="theme-assignee-select" value={themeAssignee} onChange={e=>setThemeAssignee(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400">
                <option value="">Choose a colleague…</option>
                {staffPool.map(s=><option key={s.email} value={s.email}>{s.name||s.email}</option>)}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">Rex drafts the post immediately and it lands in their scrum (Reviews requiring attention) to refine, send to design and publish.</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" data-testid="theme-draft-myself" disabled={themeBusy} onClick={draftThemeMyself}>
              {themeBusy ? "Starting…" : "Draft it myself now"}
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="theme-assign-draft" disabled={!themeAssignee||themeBusy} onClick={assignThemeDraft}>
              {themeBusy ? "Assigning…" : "Assign & draft with Rex"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!signalHistory} onOpenChange={v=>{if(!v)setSignalHistory(null);}}>
        <DialogContent className="max-w-md" data-testid="signal-history-dialog">
          <DialogHeader><DialogTitle>Signal history</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 truncate">{signalHistory?.sig?.title}</p>
          <div className="max-h-72 overflow-y-auto space-y-2" data-testid="signal-history-rows">
            {signalHistory?.loading ? <p className="text-xs text-slate-400">Loading…</p> :
              (signalHistory?.rows||[]).length===0 ? <p className="text-xs text-slate-400" data-testid="signal-history-empty">No recorded history yet.</p> :
              signalHistory.rows.map((h,hi)=>{
                const ch = h.changes || (h.field ? [{field:h.field,from:h.from,to:h.to}] : []);
                const who = h.actor_name || h.user_name || "someone";
                const when = (h.timestamp||"").slice(0,16).replace("T"," ");
                return (<div key={hi} className="text-xs border border-slate-100 rounded px-2 py-1.5">
                  <div className="font-medium text-slate-700">{h.action_label||h.action||"change"} · {who} <span className="text-slate-400">{when}</span></div>
                  {ch.map((c,ci)=><div key={ci} className="text-slate-500">{c.field}: {String(c.from??"—")} → {String(c.to??"—")}</div>)}
                </div>);
              })}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showCommit} onOpenChange={setShowCommit}><DialogContent className="max-w-md max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Commit Action</DialogTitle></DialogHeader><div className="space-y-3"><div><Label>What needs to happen?</Label><Input value={cf.title} onChange={e=>setCf(p=>({...p,title:e.target.value}))} className="text-base"/></div><div className="grid grid-cols-2 gap-3"><div><Label>Section</Label><Select value={cf.source_section} onValueChange={v=>setCf(p=>({...p,source_section:v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="operations">Operations</SelectItem><SelectItem value="client">Client</SelectItem><SelectItem value="commercial">Commercial</SelectItem><SelectItem value="marketing">Marketing</SelectItem><SelectItem value="founder">Leader</SelectItem><SelectItem value="general">General</SelectItem></SelectContent></Select></div><div><Label>Due</Label><Input type="time" value={cf.due_time} onChange={e=>setCf(p=>({...p,due_time:e.target.value}))}/></div></div><div><Label>Client</Label><Input value={cf.client_name} onChange={e=>setCf(p=>({...p,client_name:e.target.value}))} placeholder="Optional"/></div>{senders.length>0&&<div><Label>Send as</Label><Select value={cf.sender_id} onValueChange={v=>setCf(p=>({...p,sender_id:v}))}><SelectTrigger><SelectValue placeholder="Select sender..."/></SelectTrigger><SelectContent>{senders.map(s=><SelectItem key={s.sender_id} value={s.sender_id}>{s.display_name} ({s.email})</SelectItem>)}</SelectContent></Select></div>}{senders.length===0&&<button className="text-xs text-blue-600 hover:underline" onClick={()=>{setShowCommit(false);setShowAddSender(true);}}>+ Add authorized sender</button>}<label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cf.founder_escalation} onChange={e=>setCf(p=>({...p,founder_escalation:e.target.checked}))} className="rounded"/>Leader decision</label></div><DialogFooter><Button variant="outline" onClick={()=>setShowCommit(false)}>Cancel</Button><Button disabled={!cf.title} onClick={async()=>{await post("/scrum/v2/commitments",cf);toast.success("Committed");setShowCommit(false);}}>Commit</Button></DialogFooter></DialogContent></Dialog>

      {/* Inline Content Draft Editor — edit & send-to-designer without leaving Morning Scrum OS */}
      <Dialog open={!!showContentDraftEditor} onOpenChange={()=>{ if(!contentDraftSaving && !contentDraftSending) setShowContentDraftEditor(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="content-draft-editor">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-blue-600"/>
              {showContentDraftEditor?.context_title||"Edit Draft"}
            </DialogTitle>
          </DialogHeader>
          {showContentDraftEditor && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">{showContentDraftEditor.post_type==="company"?"Company Post":"Personal / Leader"}</span>
                <span className={`px-2 py-0.5 rounded font-semibold ${showContentDraftEditor.status==="sent_to_designer"?"bg-purple-100 text-purple-700":"bg-blue-100 text-blue-700"}`}>{(showContentDraftEditor.status||"").replace(/_/g," ")}</span>
                {!designerConfig.configured && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700">Designer email not set</span>}
                {designerConfig.configured && <span className="text-slate-500">Designer: <span className="font-semibold text-slate-700">{designerConfig.designer_name||designerConfig.designer_email}</span></span>}
              </div>
              <div>
                <Label className="text-xs">Post content</Label>
                <Textarea value={contentDraftBody} onChange={e=>setContentDraftBody(e.target.value)} rows={14} className="font-mono text-sm leading-relaxed" placeholder="Edit the post content..." data-testid="content-draft-body"/>
                <p className="text-[10px] text-slate-400 mt-1">{contentDraftBody.length} characters · Rex will learn from your edits for future drafts.</p>
              </div>
              <div className="rounded-lg border-2 border-dashed border-purple-200 bg-purple-50/40 px-3 py-2.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Zap className="h-3.5 w-3.5 text-purple-600"/>
                  <Label className="text-[11px] font-bold uppercase tracking-wider text-purple-700">Refine with REX</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={contentDraftRefineInstr}
                    onChange={e=>setContentDraftRefineInstr(e.target.value)}
                    onKeyDown={e=>{ if(e.key==="Enter" && !e.shiftKey && contentDraftRefineInstr.trim() && !contentDraftRefining){ e.preventDefault(); refineContentDraft(); } }}
                    placeholder="e.g. Make this punchier, lead with the African angle"
                    className="h-9 text-sm bg-white"
                    disabled={contentDraftRefining||contentDraftSaving||contentDraftSending}
                    data-testid="content-draft-refine-input"
                  />
                  <Button size="sm" onClick={refineContentDraft} disabled={contentDraftRefining||!contentDraftRefineInstr.trim()||contentDraftSaving||contentDraftSending} className="bg-purple-600 hover:bg-purple-700 text-white shrink-0" data-testid="content-draft-refine-btn">
                    {contentDraftRefining ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Zap className="h-3.5 w-3.5 mr-1"/>Refine</>}
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">REX rewrites the body in place using your instruction. UK spelling, no em-dashes.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <Label className="text-xs">Designer deadline</Label>
                  <Input type="date" value={contentDraftDeadline} onChange={e=>setContentDraftDeadline(e.target.value)} data-testid="content-draft-deadline"/>
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed">Sending creates / updates a card in <span className="font-semibold">Content Studio → Production Board</span> in the <span className="font-semibold text-blue-700">READY FOR DESIGN</span> column.</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={()=>setShowContentDraftEditor(null)} disabled={contentDraftSaving||contentDraftSending}>Cancel</Button>
            <Button variant="outline" onClick={saveContentDraft} disabled={contentDraftSaving||contentDraftSending} data-testid="content-draft-save-btn">
              {contentDraftSaving?<Loader2 className="h-4 w-4 mr-1 animate-spin"/>:null}Save
            </Button>
            <Button onClick={sendContentDraftToDesigner} disabled={contentDraftSending||contentDraftSaving||!designerConfig.configured} className="bg-purple-600 text-white hover:bg-purple-700" data-testid="content-draft-send-btn">
              {contentDraftSending?<Loader2 className="h-4 w-4 mr-1 animate-spin"/>:<Palette className="h-4 w-4 mr-1"/>}
              Send to Designer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Add Occasion — reuses POST /content-calendar/markers; mirrors Add Event */}
      <Dialog open={showAddOccasion} onOpenChange={(v)=>{ if(!v) setShowAddOccasion(false); }}>
        <DialogContent className="max-w-md" data-testid="add-occasion-dialog">
          <DialogHeader><DialogTitle>Add an occasion</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input data-testid="add-occasion-title" value={newOccasion.title} onChange={e=>setNewOccasion(p=>({...p,title:e.target.value}))} placeholder="e.g., Client 10-year anniversary"/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Date *</Label><Input type="date" data-testid="add-occasion-date" value={newOccasion.date} onChange={e=>setNewOccasion(p=>({...p,date:e.target.value}))}/></div>
              <div><Label>Type</Label><Input data-testid="add-occasion-type" value={newOccasion.type} onChange={e=>setNewOccasion(p=>({...p,type:e.target.value}))} placeholder="custom / holiday / milestone"/></div>
            </div>
            <div><Label>Country</Label><Input data-testid="add-occasion-country" value={newOccasion.country} onChange={e=>setNewOccasion(p=>({...p,country:e.target.value}))} placeholder="Nigeria"/></div>
            <div><Label>Notes</Label><Textarea data-testid="add-occasion-notes" value={newOccasion.notes} onChange={e=>setNewOccasion(p=>({...p,notes:e.target.value}))} rows={2}/></div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="add-occasion-content-flag" checked={newOccasion.content_opportunity} onChange={e=>setNewOccasion(p=>({...p,content_opportunity:e.target.checked}))}/> A content opportunity</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowAddOccasion(false)}>Cancel</Button>
            <Button data-testid="add-occasion-submit" disabled={!newOccasion.title||!newOccasion.date} onClick={addOccasion}>Add occasion</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The filtered At Risk queue — the doorway behind the strip's counts. Reuses the
          SAME AtRiskRow + adapter registry + triage control, so a filtered item has
          exactly the capabilities it has on Today. No separate actions or mutations. */}
      <Dialog open={!!atRiskFilter} onOpenChange={(v)=>{ if(!v){ setAtRiskFilter(null); setAtRiskQueue({ items: [], total_items: 0, loading: false }); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="at-risk-queue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600"/>
              {atRiskFilter?.label || "At Risk"}
              <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-bold" data-testid="at-risk-queue-count">{atRiskQueue.total_items} items</span>
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">The full At Risk queue for {atRiskFilter?.label || "all items"}. Each item can be triaged in place or opened.</DialogDescription>
          <div className="space-y-1.5">
            {atRiskQueue.loading ? (
              <p className="text-sm text-slate-400 py-6 text-center" data-testid="at-risk-queue-loading">Loading the full queue…</p>
            ) : atRiskQueue.items.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center" data-testid="at-risk-queue-empty">Nothing here — this queue is clear.</p>
            ) : atRiskQueue.items.map((r,qi)=>(
              <AtRiskRow
                key={`${r.type}-${r.target_id}-${qi}`}
                item={r}
                idx={`q${qi}`}
                api={API}
                users={commitUsers}
                onDone={refreshAtRiskQueue}
                onOpenClient={(cid) => openClient(cid)}
                onOpenBoard={() => navigate(`/scrum`)}
                onOpen={openAtRiskItem}
                onChase={openChaseDialog}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Event — reuses /events/create; added on Today per user request */}
      <Dialog open={showAddEvent} onOpenChange={(v)=>{ if(!v) setShowAddEvent(false); }}>
        <DialogContent className="max-w-md" data-testid="add-event-dialog">
          <DialogHeader><DialogTitle>Add an event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="add-event-name" value={newEvent.name} onChange={e=>setNewEvent(p=>({...p,name:e.target.value}))} placeholder="e.g., Africa Fintech Summit Kigali 2026"/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Start date *</Label><Input type="date" data-testid="add-event-date" value={newEvent.date_start} onChange={e=>setNewEvent(p=>({...p,date_start:e.target.value}))}/></div>
              <div><Label>Country *</Label><Input data-testid="add-event-country" value={newEvent.country} onChange={e=>setNewEvent(p=>({...p,country:e.target.value}))} placeholder="Nigeria"/></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>City</Label><Input data-testid="add-event-city" value={newEvent.city} onChange={e=>setNewEvent(p=>({...p,city:e.target.value}))} placeholder="Lagos"/></div>
              <div><Label>Link</Label><Input data-testid="add-event-url" value={newEvent.url} onChange={e=>setNewEvent(p=>({...p,url:e.target.value}))} placeholder="https://"/></div>
            </div>
            <div><Label>Description</Label><Textarea data-testid="add-event-description" value={newEvent.description} onChange={e=>setNewEvent(p=>({...p,description:e.target.value}))} rows={2}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowAddEvent(false)}>Cancel</Button>
            <Button data-testid="add-event-submit" disabled={!newEvent.name||!newEvent.date_start||!newEvent.country} onClick={addEvent}>Add event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event detail dialog — drill-down for Upcoming Events */}
      <Dialog open={!!eventDetail} onOpenChange={()=>setEventDetail(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="event-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-start gap-2">
              <Clock className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0"/>
              <span className="text-base">{eventDetail?.name}</span>
            </DialogTitle>
          </DialogHeader>
          {eventDetail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                {eventDetail.relevance?.score!==undefined && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${eventDetail.relevance.score>=70?"bg-emerald-200 text-emerald-800":eventDetail.relevance.score>=50?"bg-amber-100 text-amber-700":"bg-slate-100 text-slate-600"}`}>
                    Relevance {eventDetail.relevance.score}/100
                  </span>
                )}
                {eventDetail.country && <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{eventDetail.country}</span>}
                {eventDetail.source && <span className="text-xs px-2 py-0.5 rounded bg-slate-50 text-slate-500">{eventDetail.source==="verified"?"Verified source":"Auto-discovered"}</span>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-slate-500 uppercase tracking-wider text-[10px] font-bold mb-0.5">Date</p>
                  <p className="text-slate-900 font-semibold">{fmtDate(eventDetail.date_start)}{eventDetail.date_end && eventDetail.date_end!==eventDetail.date_start ? ` → ${fmtDate(eventDetail.date_end)}` : ""}</p>
                </div>
                <div>
                  <p className="text-slate-500 uppercase tracking-wider text-[10px] font-bold mb-0.5">Location</p>
                  <p className="text-slate-900 font-semibold">{[eventDetail.city, eventDetail.location, eventDetail.country].filter(Boolean).join(" · ") || "—"}</p>
                </div>
              </div>
              {eventDetail.description && (
                <div>
                  <p className="text-slate-500 uppercase tracking-wider text-[10px] font-bold mb-1">Description</p>
                  <p className="text-slate-700 leading-relaxed">{eventDetail.description}</p>
                </div>
              )}
              {(eventDetail.sectors?.length>0 || eventDetail.themes?.length>0) && (
                <div>
                  <p className="text-slate-500 uppercase tracking-wider text-[10px] font-bold mb-1">Sectors / Themes</p>
                  <div className="flex flex-wrap gap-1">
                    {(eventDetail.sectors||[]).map((s,i)=><span key={`s${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{s}</span>)}
                    {(eventDetail.themes||[]).map((t,i)=><span key={`t${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">{t}</span>)}
                  </div>
                </div>
              )}
              {eventDetail.relevance?.reasons?.length>0 && (
                <div>
                  <p className="text-slate-500 uppercase tracking-wider text-[10px] font-bold mb-1">Why REX flagged this</p>
                  <ul className="list-disc list-inside text-slate-700 space-y-0.5">
                    {eventDetail.relevance.reasons.map((r,i)=><li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
              {eventDetail.recommended_posture && (
                <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
                  <p className="text-indigo-900 text-xs font-bold uppercase tracking-wider mb-1">REX recommends: {eventDetail.recommended_posture.recommended}</p>
                  <p className="text-indigo-700 text-xs leading-relaxed">{eventDetail.recommended_posture.reason}</p>
                  {eventDetail.recommended_posture.actions?.length>0 && (
                    <ul className="list-disc list-inside text-indigo-700 text-xs mt-1.5 space-y-0.5">
                      {eventDetail.recommended_posture.actions.map((a,i)=><li key={i}>{a}</li>)}
                    </ul>
                  )}
                </div>
              )}
              {(eventDetail.url || eventDetail.source_url) && (
                <a href={eventDetail.url||eventDetail.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="event-source-link">
                  <ExternalLink className="h-3.5 w-3.5"/>Open source page
                </a>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={()=>setEventDetail(null)}>Close</Button>
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="event-detail-dismiss-btn"
              onClick={async()=>{
                if (!eventDetail?.event_id) { toast.error("Event id missing"); return; }
                const r = await post(`/events/${eventDetail.event_id}/dismiss`, {});
                if (r.ok) {
                  toast.success("Dismissed");
                  setEvents(p=>p.filter(x=>x.event_id!==eventDetail.event_id));
                  setEventDetail(null);
                } else toast.error("Dismiss failed");
              }}>
              <X className="h-4 w-4 mr-1"/>Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Designer Heatmap detail dialog */}
      <Dialog open={!!heatmapDetail} onOpenChange={()=>setHeatmapDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="heatmap-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-indigo-600"/>
              {heatmapDetail?.designer_name||"Unassigned"}
              <span className={`text-xs px-2 py-0.5 rounded font-bold ${heatmapDetail?.status==="red"?"bg-red-100 text-red-700":heatmapDetail?.status==="amber"?"bg-amber-100 text-amber-700":"bg-emerald-100 text-emerald-700"}`}>{(heatmapDetail?.status||"").toUpperCase()}</span>
            </DialogTitle>
          </DialogHeader>
          {heatmapDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-slate-50 p-2"><p className="text-2xl font-bold text-slate-900">{heatmapDetail.active_load}</p><p className="text-[10px] text-slate-500 uppercase">Active</p></div>
                <div className="rounded-lg bg-red-50 p-2"><p className="text-2xl font-bold text-red-700">{heatmapDetail.overdue}</p><p className="text-[10px] text-slate-500 uppercase">Overdue</p></div>
                <div className="rounded-lg bg-emerald-50 p-2"><p className="text-2xl font-bold text-emerald-700">{heatmapDetail.throughput_7d}</p><p className="text-[10px] text-slate-500 uppercase">Shipped 7d</p></div>
              </div>
              {heatmapDetail.overdue_cards?.length>0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-red-600 mb-2">Overdue ({heatmapDetail.overdue_cards.length})</p>
                  <div className="space-y-1.5">
                    {heatmapDetail.overdue_cards.map((c,i)=>(
                      <div key={i} className="rounded border border-red-200 bg-red-50/40 px-2.5 py-1.5">
                        <p className="text-sm font-semibold text-slate-900 truncate">{c.title}</p>
                        <p className="text-[11px] text-slate-500">{c.stage} · due {c.due_date}{c.client_name?` · ${c.client_name}`:""}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {heatmapDetail.active_cards?.length>0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Active ({heatmapDetail.active_cards.length})</p>
                  <div className="space-y-1.5">
                    {heatmapDetail.active_cards.map((c,i)=>(
                      <div key={i} className="rounded border border-slate-200 bg-white px-2.5 py-1.5">
                        <p className="text-sm font-semibold text-slate-900 truncate">{c.title}</p>
                        <p className="text-[11px] text-slate-500">{c.stage} · due {c.due_date||"—"}{c.client_name?` · ${c.client_name}`:""}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setHeatmapDetail(null)}>Close</Button>
            {heatmapDetail?.overdue>0 && (
              <Button variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="heatmap-nudge-btn" onClick={openNudgeDialog}>
                <Mail className="h-4 w-4 mr-1"/>Send Overdue Nudge
              </Button>
            )}
            <Button onClick={()=>{setHeatmapDetail(null);navigate("/amplify");}} data-testid="heatmap-detail-open-board-btn">Open Board</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REX-drafted Designer Nudge dialog */}
      {/* Chase-email composer for a stalled "Awaiting Client Response" task */}
      <Dialog open={chaseOpen} onOpenChange={(open)=>{ if(!chaseSending && !chaseDrafting) setChaseOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="chase-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-purple-600"/>Chase {chase.client_name || "the client"}</DialogTitle>
            <DialogDescription>Rex drafted this follow-up. Review it, then send from a verified sender — it's logged on the client automatically.</DialogDescription>
          </DialogHeader>
          {chaseDrafting ? (
            <div className="py-10 text-center text-sm text-slate-500"><Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin"/>Rex is drafting the chase…</div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">To</Label>
                  <Input value={chase.to_email} onChange={e=>setChase(c=>({...c,to_email:e.target.value}))} placeholder="client@company.com" data-testid="chase-to"/>
                  {chase.to_name && <p className="text-[11px] text-slate-400 mt-0.5">{chase.to_name}</p>}
                </div>
                <div>
                  <Label className="text-xs">Send from</Label>
                  <Select value={chase.sender_id} onValueChange={v=>setChase(c=>({...c,sender_id:v}))}>
                    <SelectTrigger data-testid="chase-sender"><SelectValue placeholder="Pick a sender"/></SelectTrigger>
                    <SelectContent>
                      {(chase.senders||[]).map(s=><SelectItem key={s.sender_id} value={s.sender_id}>{s.display_name || s.email} · {s.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={chase.subject} onChange={e=>setChase(c=>({...c,subject:e.target.value}))} className="text-base" data-testid="chase-subject"/>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Message</Label>
                  <Button size="sm" variant="outline" onClick={redraftChase} disabled={chaseDrafting||chaseSending} data-testid="chase-redraft-btn"><RefreshCw className="h-3 w-3 mr-1"/>Redraft</Button>
                </div>
                <Textarea value={chase.body} onChange={e=>setChase(c=>({...c,body:e.target.value}))} rows={11} className="text-sm leading-relaxed" data-testid="chase-body"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setChaseOpen(false)} disabled={chaseSending}>Cancel</Button>
            <Button onClick={sendChase} disabled={chaseSending||chaseDrafting||!chase.subject.trim()||!chase.body.trim()||!chase.to_email.trim()||!chase.sender_id} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="chase-send-btn">
              {chaseSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Send className="h-4 w-4 mr-1"/>}Send chase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={nudgeOpen} onOpenChange={(open)=>{ if(!nudgeSending && !nudgeDrafting) setNudgeOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="nudge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-amber-600"/>
              Nudge {heatmapDetail?.designer_name||"Designer"}
              <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold">{heatmapDetail?.overdue||0} overdue</span>
            </DialogTitle>
          </DialogHeader>
          {nudgeDrafting ? (
            <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="h-6 w-6 mr-2 animate-spin"/>REX is drafting...</div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tone</Label>
                  <Select value={nudgeTone} onValueChange={setNudgeTone}>
                    <SelectTrigger data-testid="nudge-tone"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="friendly">Friendly · supportive</SelectItem>
                      <SelectItem value="firm">Firm · respectful</SelectItem>
                      <SelectItem value="urgent">Urgent · same-day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Extra context (optional)</Label>
                  <Input value={nudgeExtra} onChange={e=>setNudgeExtra(e.target.value)} placeholder="e.g. workshop next week, board meeting Friday" data-testid="nudge-extra"/>
                </div>
              </div>
              <div>
                <Button size="sm" variant="outline" onClick={redraftNudge} disabled={nudgeDrafting||nudgeSending} data-testid="nudge-redraft-btn">
                  <RefreshCw className="h-3.5 w-3.5 mr-1"/>Redraft with new tone / context
                </Button>
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={nudgeSubject} onChange={e=>setNudgeSubject(e.target.value)} className="text-base" data-testid="nudge-subject"/>
              </div>
              <div>
                <Label className="text-xs">Body (you can edit before sending)</Label>
                <Textarea value={nudgeBody} onChange={e=>setNudgeBody(e.target.value)} rows={11} className="text-sm leading-relaxed" data-testid="nudge-body"/>
                <p className="text-[10px] text-slate-400 mt-1">{nudgeBody.length} characters · The list of overdue items will be appended automatically.</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={()=>setNudgeOpen(false)} disabled={nudgeSending}>Cancel</Button>
            <Button onClick={sendNudge} disabled={nudgeSending||nudgeDrafting||!nudgeSubject.trim()||!nudgeBody.trim()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="nudge-send-btn">
              {nudgeSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Send className="h-4 w-4 mr-1"/>}
              Send Nudge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showAddSender} onOpenChange={setShowAddSender}><DialogContent className="max-w-md max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Add Authorized Sender</DialogTitle></DialogHeader><div className="space-y-3"><div><Label>Name</Label><Input value={newSender.display_name} onChange={e=>setNewSender(p=>({...p,display_name:e.target.value}))} placeholder="e.g., Remi Dairo"/></div><div><Label>Email</Label><Input value={newSender.email} onChange={e=>setNewSender(p=>({...p,email:e.target.value}))} placeholder="e.g., remi@resiligent.com"/></div><div><Label>Title</Label><Input value={newSender.title} onChange={e=>setNewSender(p=>({...p,title:e.target.value}))} placeholder="e.g., Managing Partner"/></div></div><DialogFooter><Button variant="outline" onClick={()=>setShowAddSender(false)}>Cancel</Button><Button disabled={!newSender.email||!newSender.display_name} onClick={async()=>{const r=await post("/mission-control/senders",newSender);if(r.ok){toast.success("Sender added");setShowAddSender(false);setNewSender({email:"",display_name:"",title:""});fetchAll();}else{const e=await r.json().catch(()=>({}));toast.error(e.detail||"Failed");}}}>Add Sender</Button></DialogFooter></DialogContent></Dialog>

      <Dialog open={!!showNote} onOpenChange={()=>setShowNote(null)}><DialogContent className="max-w-md max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Update Client</DialogTitle></DialogHeader><Textarea value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="What happened? Next steps?" rows={3} className="text-base"/><DialogFooter><Button variant="outline" onClick={()=>setShowNote(null)}>Cancel</Button><Button onClick={async()=>{if(showNote&&noteText.trim()){await Promise.all([post("/notes",{parent_id:showNote,parent_type:"client",content:noteText}),put(`/clients/${showNote}`,{last_contacted_date:new Date().toISOString()})]);toast.success("Client updated + note saved");
// Update client motion card locally so it reflects immediately
setClientMotion(p=>p.map(cm=>cm.client_id===showNote?{...cm,primary_motion:`Note: ${noteText.substring(0,100)}`,days_since_contact:0,updated_at:new Date().toISOString(),motions:[{type:"delivery",summary:`Note: ${noteText.substring(0,100)}`},...(cm.motions||[])]}:cm));
setShowNote(null);}}}>Save</Button></DialogFooter></DialogContent></Dialog>

      <Dialog open={!!showArticle} onOpenChange={()=>setShowArticle(null)}><DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle className="text-lg">{showArticle?.headline||showArticle?.title}</DialogTitle></DialogHeader>{showArticle&&<div className="space-y-4"><div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">{showArticle.analyzed_at&&<span className="font-medium">{fmtDate(showArticle.analyzed_at)}</span>}{(showArticle.organisations||[]).length>0&&<span>| {showArticle.organisations.join(", ")}</span>}{showArticle.country&&<span>| {showArticle.country}</span>}{showArticle.category&&<span>| {showArticle.category?.replace(/_/g," ")}</span>}</div><p className="text-base text-slate-700 leading-relaxed">{showArticle.importance||showArticle.summary||showArticle.description||""}</p>{(showArticle.source_url||showArticle.url)&&<a href={showArticle.source_url||showArticle.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-base text-blue-600 hover:underline font-medium">Read full article <ExternalLink className="h-4 w-4"/></a>}<div className="border-t pt-3"><Label className="text-sm font-semibold">Tell Rex what to do with this</Label><Textarea value={rexInstruction} onChange={e=>setRexInstruction(e.target.value)} placeholder="e.g., Draft an outreach email, research the company..." rows={3} className="mt-1 text-base"/></div><div className="flex gap-2 flex-wrap"><Button onClick={()=>{if(rexInstruction.trim()){post("/ask-rex/chat",{message:rexInstruction+" Context: "+JSON.stringify({headline:showArticle.headline,company:(showArticle.organisations||[])[0]||"",country:showArticle.country}),session_id:"scrum_instruction"});toast.success("Rex is working on it");}setShowActionRoute(showArticle);setActionRouteChoice("");setRexInstruction("");setShowArticle(null);}}>Accept for Action</Button><Button variant="outline" onClick={()=>{if(rexInstruction.trim()){post("/ask-rex/chat",{message:"Create content: "+rexInstruction,session_id:"scrum_content"});}commit(showArticle.headline||showArticle.title||"","marketing");setRexInstruction("");setShowArticle(null);}}>Convert to Content</Button><Button variant="outline" className="text-purple-700 border-purple-300" onClick={()=>{commit(showArticle.headline||showArticle.title||"","founder");setRexInstruction("");setShowArticle(null);}}>Send to Leader</Button><Button variant="ghost" onClick={()=>{dismissNews(showArticle.news_id||showArticle.article_id||"");setRexInstruction("");setShowArticle(null);}}>Dismiss</Button></div></div>}</DialogContent></Dialog>

      {/* Action Route Dialog — What should Rex do with this news? */}
      <Dialog open={!!showActionRoute} onOpenChange={()=>setShowActionRoute(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg">What should Rex do with this?</DialogTitle></DialogHeader>
          {showActionRoute && <div className="space-y-3">
            <div className="bg-slate-50 rounded-lg px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">{showActionRoute.headline}</p>
              <p className="text-xs text-slate-500 mt-1">{(showActionRoute.organisations || []).join(", ") || showActionRoute.company || ""} | {showActionRoute.country}</p>
            </div>
            <div className="space-y-1.5">
              {[
                { id: "research_crm", label: "Research & Add to CRM", desc: "Rex researches the company, creates/enriches a client record, finds contacts, notes the commercial opportunity", color: "border-blue-200 bg-blue-50" },
                { id: "add_to_pipeline", label: "Add to Pipeline", desc: "Create an unconfirmed opportunity row with an AI-estimated deal value. Confirm or edit it from the Pipeline tab in Revenue.", color: "border-indigo-200 bg-indigo-50" },
                { id: "conversation_entry", label: "Generate Conversation Entry", desc: "Rex produces an opening line, context reference, POV and tone for a peer-to-peer conversation — LinkedIn, call, or email", color: "border-green-200 bg-green-50" },
                { id: "create_content", label: "Create Authority Content", desc: "Creates a LinkedIn post or article in the Distribution Flywheel based on this market intelligence", color: "border-purple-200 bg-purple-50" },
                { id: "brief_team", label: "Brief the Team", desc: "Adds to tomorrow's scrum briefing so the team is aware of this development", color: "border-amber-200 bg-amber-50" },
                { id: "track_only", label: "Just Track It", desc: "Creates a commitment to follow up — no immediate Rex action", color: "border-slate-200 bg-slate-50" },
              ].map(opt => (
                <button key={opt.id} onClick={() => setActionRouteChoice(opt.id)}
                  data-testid={`action-option-${opt.id}`}
                  className={`w-full text-left rounded-lg px-3 py-2 border-2 transition-all ${actionRouteChoice === opt.id ? opt.color + " ring-2 ring-blue-400" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                  <p className="text-sm font-semibold text-slate-900">{opt.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{opt.desc}</p>
                </button>
              ))}
            </div>
            {actionRouteChoice === "conversation_entry" && (
              <div className="text-xs text-slate-500 bg-green-50 border border-green-100 rounded p-2">
                Rex will generate: opening line, context reference, POV, tone, and follow-up angles — ready to copy into LinkedIn, call prep, or email.
              </div>
            )}
          </div>}
          <DialogFooter className="sticky bottom-0 bg-white pt-3 border-t border-slate-100">
            <Button variant="outline" onClick={() => setShowActionRoute(null)}>Cancel</Button>
            <Button disabled={!actionRouteChoice || convEntryLoading} onClick={handleActionRoute} data-testid="action-submit-btn">
              {convEntryLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : null}
              {actionRouteChoice === "research_crm" ? "Research Now" :
               actionRouteChoice === "add_to_pipeline" ? "Add to Pipeline" :
               actionRouteChoice === "conversation_entry" ? "Generate Entry" :
               actionRouteChoice === "create_content" ? "Create Content" :
               actionRouteChoice === "brief_team" ? "Add to Briefing" : "Commit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Draft Preview Dialog */}
      <Dialog open={!!showDraftPreview} onOpenChange={()=>{setShowDraftPreview(null);setEditingDraft(false);}}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2">
              {editingDraft ? (
                <Input value={editDraftSubject} onChange={e=>setEditDraftSubject(e.target.value)} className="text-lg font-semibold flex-1" placeholder="Email subject..." data-testid="edit-draft-subject"/>
              ) : (
                <DialogTitle className="flex-1">{showDraftPreview?.subject||"Draft Email"}</DialogTitle>
              )}
              {showDraftPreview?.status==="draft"&&!editingDraft&&(
                <Button variant="outline" size="sm" className="shrink-0 gap-1" onClick={()=>{setEditingDraft(true);setEditDraftSubject(showDraftPreview.subject||"");setEditDraftBody(showDraftPreview.body_plain||showDraftPreview.body_html?.replace(/<[^>]+>/g,"")||"");}} data-testid="edit-draft-btn">
                  <Edit3 className="h-3.5 w-3.5"/>Edit
                </Button>
              )}
            </div>
          </DialogHeader>
          {showDraftPreview&&<div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${showDraftPreview.status==="draft"?"bg-amber-100 text-amber-700":showDraftPreview.status==="approved"?"bg-green-100 text-green-700":"bg-blue-100 text-blue-700"}`}>{showDraftPreview.status?.replace(/_/g," ")}</span>
              {showDraftPreview.sender_name&&<span>From: {showDraftPreview.sender_name} ({showDraftPreview.sender_email})</span>}
              {showDraftPreview.company_name&&<span>| To: {showDraftPreview.company_name}</span>}
            </div>
            {/* G4 — gate + exits for a stuck campaign */}
            {["draft","pending_sender_verification","approved","held"].includes(showDraftPreview.status)&&(()=>{
              const s=showDraftPreview.status;
              const gate = s==="draft"?{r:"Awaiting review — not yet approved to send.",c:"Send it, cancel with a reason, or hold to a date."}
                : s==="pending_sender_verification"?{r:"The sender address isn't verified yet.",c:"Enter the verification code, then send."}
                : s==="approved"?{r:"Approved but not yet sent.",c:"Send it now, or hold to a date."}
                : {r:`Held until ${showDraftPreview.hold_until||""}.`,c:"Returns to the queue on its hold date, or send now."};
              return (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="campaign-gate">
                <p className="text-xs font-semibold text-amber-800">Why it's waiting: <span className="font-normal">{gate.r}</span></p>
                <p className="text-xs text-amber-700 mt-0.5">What clears it: {gate.c}</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" className="h-7 text-[11px] text-amber-700 border-amber-300" onClick={()=>setCampDecision({kind:"hold",reason:"",date:""})} data-testid="campaign-hold-btn">Hold until…</Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-300" onClick={()=>setCampDecision({kind:"cancel",reason:"",date:""})} data-testid="campaign-cancel-btn">Cancel…</Button>
                </div>
                {campDecision.kind&&(
                  <div className="mt-2 p-2 bg-white border border-amber-200 rounded" data-testid="campaign-decision-form">
                    {campDecision.kind==="hold"&&(
                      <input type="date" value={campDecision.date} onChange={e=>setCampDecision(d=>({...d,date:e.target.value}))} className="text-sm border rounded px-2 py-1 mb-2 w-full" data-testid="campaign-decision-date"/>
                    )}
                    <textarea value={campDecision.reason} onChange={e=>setCampDecision(d=>({...d,reason:e.target.value}))} rows={2} placeholder={campDecision.kind==="cancel"?"Why cancel? (required)":"Note (optional)"} className="text-sm border rounded px-2 py-1 w-full" data-testid="campaign-decision-reason"/>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="h-7 text-[11px]" disabled={(campDecision.kind==="cancel"&&!campDecision.reason.trim())||(campDecision.kind==="hold"&&!campDecision.date)} onClick={async()=>{
                        const ep=campDecision.kind==="cancel"?"cancel":"hold";
                        const r=await post(`/mission-control/campaigns/${showDraftPreview.campaign_id}/${ep}`,{reason:campDecision.reason.trim(),hold_until:campDecision.date});
                        const b=await r.json().catch(()=>({}));
                        if(!r.ok){toast.error(b.detail||"Could not apply");return;}
                        toast.success(campDecision.kind==="cancel"?"Campaign cancelled":`Held until ${campDecision.date}`);
                        setCampaigns(p=>p.filter(c=>c.campaign_id!==showDraftPreview.campaign_id));
                        setShowDraftPreview(null);setCampDecision({kind:null,reason:"",date:""});
                      }} data-testid="campaign-decision-confirm">{campDecision.kind==="cancel"?"Confirm cancel":"Confirm hold"}</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={()=>setCampDecision({kind:null,reason:"",date:""})} data-testid="campaign-decision-cancel">Back</Button>
                    </div>
                  </div>
                )}
              </div>);
            })()}
            {showDraftPreview.recipients?.length>0&&<div className="bg-blue-50 rounded-lg px-4 py-2">
              <p className="text-xs font-semibold text-blue-700 mb-1">Recipients</p>
              {showDraftPreview.recipients.map((r,i)=><p key={i} className="text-sm text-blue-800">{r.name||"?"} — {r.email} <span className="text-blue-500">({r.title||""}) [{r.source||"manual"}]</span></p>)}
            </div>}
            {editingDraft ? (
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500">Email Body</Label>
                <Textarea value={editDraftBody} onChange={e=>setEditDraftBody(e.target.value)} rows={12} className="text-sm font-mono leading-relaxed" placeholder="Edit email body..." data-testid="edit-draft-body"/>
                <p className="text-xs text-slate-400">Plain text edit — formatting will be preserved when sent</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-slate-500 mb-2">Email Body</p>
                <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{__html:showDraftPreview.body_html||"<p>No body</p>"}}/>
              </div>
            )}
            {!editingDraft&&showDraftPreview.talking_points?.length>0&&<div className="bg-slate-50 rounded-lg px-4 py-3">
              <p className="text-xs font-semibold text-slate-600 mb-1">Talking Points (for follow-up call)</p>
              <ul className="list-disc pl-4 space-y-1">{showDraftPreview.talking_points.map((tp,i)=><li key={i} className="text-sm text-slate-700">{tp}</li>)}</ul>
            </div>}
            <div className="flex gap-2 pt-2 border-t">
              {editingDraft ? (<>
                <Button className="bg-blue-600 text-white" data-testid="save-draft-btn" onClick={async()=>{
                  const bodyHtml = editDraftBody.split("\n").map(line=>`<p>${line}</p>`).join("");
                  const r=await put(`/mission-control/campaigns/${showDraftPreview.campaign_id}`,{subject:editDraftSubject,body_html:bodyHtml});
                  if(r.ok){const d=await r.json();toast.success("Draft updated");setCampaigns(p=>p.map(x=>x.campaign_id===showDraftPreview.campaign_id?{...x,subject:editDraftSubject,body_html:bodyHtml}:x));setShowDraftPreview({...showDraftPreview,subject:editDraftSubject,body_html:bodyHtml});setEditingDraft(false);}
                  else{const e=await r.json().catch(()=>({}));toast.error(e.detail||"Failed to save");}
                }}>Save Changes</Button>
                <Button variant="outline" onClick={()=>setEditingDraft(false)}>Cancel Edit</Button>
              </>) : (<>
                {showDraftPreview.status==="draft"&&senders.length>0&&<Button className="bg-green-600 text-white" onClick={async()=>{
                  const r=await post(`/mission-control/campaigns/${showDraftPreview.campaign_id}/approve`,{sender_id:senders[0].sender_id,recipients:showDraftPreview.recipients||[]});
                  if(r.ok){const d=await r.json();toast.success(d.message||"Verification code sent");setCampaigns(p=>p.map(x=>x.campaign_id===showDraftPreview.campaign_id?{...x,status:"pending_sender_verification"}:x));setShowDraftPreview(null);}
                  else{const e=await r.json().catch(()=>({}));toast.error(e.detail||"Failed");}
                }}>Approve & Send Verification Code</Button>}
                {showDraftPreview.status==="pending_sender_verification"&&<Button className="bg-blue-600 text-white" onClick={()=>{setShowVerifyCode(showDraftPreview);setVerifyCode("");setShowDraftPreview(null);}}>Enter Verification Code</Button>}
                <Button data-testid="campaign-give-btn" variant="default" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={()=>{
                  const plain = showDraftPreview.body_plain || (showDraftPreview.body_html||"").replace(/<[^>]+>/g," ").replace(/\s+/g," ").trim();
                  openGive({
                    title: `Send to sales: ${showDraftPreview.subject || showDraftPreview.company_name || "campaign"}`.trim(),
                    subtitle: showDraftPreview.subject || "",
                    source_kind: "campaign",
                    source_id: showDraftPreview.campaign_id || "",
                    body: `Subject: ${showDraftPreview.subject||""}\n\n${plain}`,
                    source_url: "",
                    source_label: "Campaign draft",
                    client_name: showDraftPreview.company_name || "",
                    section: "commercial",
                  });
                }}>Assign to… (send to sales)</Button>
                <Button variant="ghost" onClick={()=>setShowDraftPreview(null)}>Close</Button>
              </>)}
            </div>
          </div>}
        </DialogContent>
      </Dialog>

      {/* Verify Send Code Dialog */}
      <Dialog open={!!showVerifyCode} onOpenChange={()=>setShowVerifyCode(null)}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Enter Verification Code</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">A 6-digit code was sent to <strong>{showVerifyCode?.sender_email}</strong>. Enter it to authorise sending.</p>
          <p className="text-sm text-slate-800 font-medium mt-1">"{showVerifyCode?.subject}"</p>
          <Input value={verifyCode} onChange={e=>setVerifyCode(e.target.value)} placeholder="Enter 6-digit code" className="text-center text-2xl tracking-widest font-mono mt-2" maxLength={6}/>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowVerifyCode(null)}>Cancel</Button>
            <Button disabled={verifyCode.length!==6} onClick={async()=>{
              const r=await post(`/mission-control/campaigns/${showVerifyCode.campaign_id}/verify-send`,{code:verifyCode});
              if(r.ok){const d=await r.json();toast.success(d.message||"Sent!");setCampaigns(p=>p.map(x=>x.campaign_id===showVerifyCode.campaign_id?{...x,status:"approved"}:x));setShowVerifyCode(null);}
              else{const e=await r.json().catch(()=>({}));toast.error(e.detail||"Invalid code");}
            }}>Verify & Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={!!showEditTask} onOpenChange={()=>setShowEditTask(null)}><DialogContent className="max-w-md max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader><div className="space-y-3"><div><Label>Title</Label><Input value={editTaskForm.title} onChange={e=>setEditTaskForm(p=>({...p,title:e.target.value}))} className="text-base"/></div><div><Label>Owner</Label><Input value={editTaskForm.assigned_to_name} onChange={e=>setEditTaskForm(p=>({...p,assigned_to_name:e.target.value}))} placeholder="Assign to..." className="text-base"/></div><div><Label>Status</Label><Select value={editTaskForm.status} onValueChange={v=>{setEditTaskForm(p=>({...p,status:v}));setBlockAck(false);}}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="Not Started">Not Started</SelectItem><SelectItem value="In Progress">In Progress</SelectItem><SelectItem value="Proposed">Proposed</SelectItem><SelectItem value="Accepted">Accepted</SelectItem><SelectItem value="Awaiting Client Response">Awaiting Client Response</SelectItem><SelectItem value="Blocked">Blocked</SelectItem><SelectItem value="On Hold">On Hold</SelectItem></SelectContent></Select>{(editTaskForm.status==="Blocked"||editTaskForm.status==="On Hold")&&!blockAck&&(<div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2" data-testid="blocked-ours-or-theirs"><p className="text-xs font-medium text-amber-800 mb-1.5">Is the next move ours or theirs?</p><div className="flex gap-2"><Button size="sm" variant="outline" className="h-7 text-xs" data-testid="block-ours" onClick={()=>setBlockAck(true)}>Ours — keep it {editTaskForm.status}</Button><Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" data-testid="block-theirs" onClick={()=>nowWithThem(showEditTask)}>Theirs — that's a wait</Button></div></div>)}<div className="mt-2"><Button size="sm" variant="outline" className="h-7 text-xs text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="edit-now-with-them" onClick={()=>nowWithThem(showEditTask)}>Now with them — waiting on someone</Button></div></div><div><Label>Description</Label><Textarea value={editTaskForm.description} onChange={e=>setEditTaskForm(p=>({...p,description:e.target.value}))} rows={2}/></div></div><DialogFooter><Button variant="outline" onClick={()=>setShowEditTask(null)}>Cancel</Button><Button onClick={async()=>{if(showEditTask){await put(`/scrum/v2/tasks/${showEditTask.task_id}`,editTaskForm);toast.success("Task updated");announceTaskChange(showEditTask.task_id);setShowEditTask(null);setScrumTasks(p=>p.map(t=>t.task_id===showEditTask.task_id?{...t,...editTaskForm}:t));setOps(p=>p.map(t=>t.task_id===showEditTask.task_id?{...t,...editTaskForm}:t));}}}>Save</Button></DialogFooter></DialogContent></Dialog>

      {/* Close Task Dialog (requires status note) */}
      <Dialog open={!!showCloseTask} onOpenChange={()=>setShowCloseTask(null)}><DialogContent className="max-w-md max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Close Task — Status Update Required</DialogTitle></DialogHeader><p className="text-sm text-slate-600">"{showCloseTask?.title}"</p><div className="space-y-3"><div><Label>Closing note — what was the outcome?</Label><Textarea value={closeNote} onChange={e=>setCloseNote(e.target.value)} placeholder="e.g., Completed and sent to client, Cancelled because..." rows={3} className="text-base"/></div></div><DialogFooter><Button variant="outline" onClick={()=>setShowCloseTask(null)}>Cancel</Button><Button disabled={!closeNote.trim()} onClick={()=>{const t=showCloseTask;setShowCloseTask(null);setCompletingTask(t);}}>Continue…</Button></DialogFooter></DialogContent></Dialog>
      <TaskCompletionDialog task={completingTask} open={!!completingTask} initialOutcome={completingOutcome} onOpenChange={(v)=>{if(!v){setCompletingTask(null);setCompletingOutcome(null);}}} onDone={(res)=>{ if(completingTask){ removeTaskFromLists(completingTask.task_id); window.dispatchEvent(new CustomEvent("scrum:task-completed",{detail:{task_id:completingTask.task_id}})); } setCompletingTask(null); setCompletingOutcome(null); }} />

      {/* #3 — honest email activity: what was sent, when, to whom, and what is KNOWN. */}
      <Dialog open={showEmailActivity} onOpenChange={setShowEmailActivity}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-activity-modal">
          <DialogHeader><DialogTitle>Email activity — what Rex can actually see</DialogTitle></DialogHeader>
          {!emailActivity ? (
            <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
          ) : (
            <div className="space-y-3">
              <div className={`text-xs rounded px-3 py-2 ${emailActivity.webhook_confirms_delivery ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="email-activity-webhook">
                {emailActivity.webhook_summary}
              </div>
              <div className="divide-y border rounded-lg" data-testid="email-activity-list">
                {(emailActivity.messages || []).length === 0 && <p className="text-sm text-slate-400 px-3 py-4">No recent messages.</p>}
                {(emailActivity.messages || []).map((m, i) => (
                  <div key={i} className="px-3 py-2 text-sm" data-testid={`email-activity-row-${i}`}>
                    <div className="font-medium text-slate-800 truncate">{m.subject}</div>
                    <div className="text-xs text-slate-500">to {m.recipient} · {fmtDate(m.sent_at || m.created_at)}</div>
                    <div className="text-xs mt-0.5 text-slate-700">{m.known_status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Conversation Entry Dialog */}
      <Dialog open={!!showConvEntry} onOpenChange={()=>setShowConvEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="conversation-entry-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">Conversation Entry — {showConvEntry?.company||""}</DialogTitle>
          </DialogHeader>
          {showConvEntry && <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 font-semibold">{showConvEntry.entry?.suggested_channel||"LinkedIn DM"}</span>
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 italic">{showConvEntry.entry?.tone||""}</span>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-1">Opening Line</p>
              <p className="text-base text-slate-900 font-medium leading-snug" data-testid="conv-entry-opening">{showConvEntry.entry?.opening_line}</p>
              <button className="text-xs text-blue-600 hover:underline mt-2" onClick={()=>{navigator.clipboard.writeText(showConvEntry.entry?.opening_line||"");toast.success("Copied");}}>Copy</button>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Context Reference</p>
              <p className="text-sm text-slate-700 leading-snug">{showConvEntry.entry?.context_reference}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-purple-700 mb-1">Point of View</p>
              <p className="text-sm text-slate-800 leading-snug" data-testid="conv-entry-pov">{showConvEntry.entry?.point_of_view}</p>
            </div>
            {showConvEntry.entry?.follow_up_angles?.length>0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Follow-up Angles</p>
                <ul className="space-y-1">
                  {showConvEntry.entry.follow_up_angles.map((a,i)=>(
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="text-slate-400 font-mono">{i+1}.</span><span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2" data-testid="conv-save-action-form">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Save as a scrum action</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Assign to <span className="text-red-500">*</span></Label>
                  <Select value={convSave.assignee_name} onValueChange={v=>setConvSave(p=>({...p,assignee_name:v}))}>
                    <SelectTrigger data-testid="conv-save-assignee"><SelectValue placeholder="Choose a person…"/></SelectTrigger>
                    <SelectContent>
                      {assignablePool.map(a=><SelectItem key={a.email||a.name} value={a.name}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Client</Label>
                  <Input value={convSave.client_name} data-testid="conv-save-client" onChange={e=>{
                    const val=e.target.value;
                    const match=clientHealth.find(c=>c.company_name?.toLowerCase()===val.toLowerCase());
                    setConvSave(p=>({...p,client_name:val,client_id:match?.client_id||""}));
                  }} placeholder="Type client name…" list="conv-save-client-list"/>
                  <datalist id="conv-save-client-list">
                    {clientHealth.map(c=><option key={c.client_id} value={c.company_name}/>)}
                  </datalist>
                  {convSave.client_name && !convSave.client_id && <p className="text-[10px] text-amber-600 mt-0.5">No exact client match — the task will carry the name only.</p>}
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Button data-testid="conv-give-btn" variant="default" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={()=>{
                const e = showConvEntry.entry || {};
                const angles = (e.follow_up_angles||[]).map((a,i)=>`${i+1}. ${a}`).join("\n");
                const entryText = [
                  `Opening line: ${e.opening_line||""}`,
                  `Context: ${e.context_reference||""}`,
                  `Point of view: ${e.point_of_view||""}`,
                  e.suggested_channel ? `Channel: ${e.suggested_channel}${e.tone?` · ${e.tone}`:""}` : "",
                  angles ? `Follow-up angles:\n${angles}` : "",
                  showConvEntry.headline ? `\nFrom today's signal: ${showConvEntry.headline}` : "",
                ].filter(Boolean).join("\n\n");
                openGive({
                  title: `Initiate conversation: ${showConvEntry.company||""}`.trim(),
                  subtitle: showConvEntry.headline || "",
                  source_kind: "conversation",
                  source_id: showConvEntry.news_id || "",
                  body: entryText,
                  source_url: showConvEntry.source_url || "",
                  source_label: "Morning Intelligence",
                  client_name: convSave.client_name || showConvEntry.company || "",
                  section: "commercial",
                });
              }}>Assign to…</Button>
              <Button onClick={()=>{
                const txt = `${showConvEntry.entry?.opening_line||""}\n\n${showConvEntry.entry?.context_reference||""}\n\n${showConvEntry.entry?.point_of_view||""}`;
                navigator.clipboard.writeText(txt);
                toast.success("Full entry copied");
              }} data-testid="copy-full-entry-btn">Copy Full Entry</Button>
              <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={!convSave.assignee_name||convSave.saving} data-testid="conv-save-action-btn" onClick={async()=>{
                const e = showConvEntry.entry || {};
                const angles = (e.follow_up_angles||[]).map((a,i)=>`${i+1}. ${a}`).join("\n");
                const entryText = [
                  `Opening line: ${e.opening_line||""}`,
                  `Context: ${e.context_reference||""}`,
                  `Point of view: ${e.point_of_view||""}`,
                  e.suggested_channel ? `Channel: ${e.suggested_channel}${e.tone?` · ${e.tone}`:""}` : "",
                  angles ? `Follow-up angles:\n${angles}` : "",
                  showConvEntry.headline ? `\nFrom today's signal: ${showConvEntry.headline}` : "",
                ].filter(Boolean).join("\n\n");
                const due = new Date(Date.now()+86400000).toISOString().slice(0,10);
                const payload = {
                  title: `Initiate conversation: ${showConvEntry.company||""}`.trim(),
                  description: entryText,
                  category: "COMMERCIAL",
                  client_id: convSave.client_id,
                  client_name: convSave.client_name,
                  link_type: convSave.client_id ? "client" : "internal",
                  assigned_to_name: convSave.assignee_name,
                  due_date: due,
                  priority: "high",
                  status: "Proposed",
                  task_type: "general",
                };
                setConvSave(p=>({...p,saving:true}));
                const r = await post("/scrum/v2/tasks", payload);
                setConvSave(p=>({...p,saving:false}));
                if (r.ok) {
                  toast.success(`Saved as a scrum action for ${convSave.assignee_name}`);
                  if (showConvEntry.news_id) dismissNews(showConvEntry.news_id);
                  announceTaskChange();
                  const execR = await f("/mission-control/execution-board");
                  if (execR.ok) setExecBoard(await execR.json());
                  setShowConvEntry(null);
                } else {
                  const err = await r.json().catch(()=>({}));
                  toast.error(err.detail || "Could not save the action");
                }
              }}>{convSave.saving ? "Saving…" : "Save as a scrum action"}</Button>
              <Button variant="outline" onClick={()=>setShowConvEntry(null)}>Close</Button>
            </div>
          </div>}
        </DialogContent>
      </Dialog>

      {/* New Task Dialog — creates real scrum task (same data model as ScrumManagement) */}
      <Dialog open={showNewTask} onOpenChange={setShowNewTask}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="new-task-dialog">
          <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title <span className="text-red-500">*</span></Label>
              <Input value={newTaskForm.title} onChange={e=>setNewTaskForm(p=>({...p,title:e.target.value}))} placeholder="What needs to be done?" data-testid="new-task-dialog-title"/></div>
            <div>
              <Label className="text-xs mb-1.5 block">Category <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-2" data-testid="new-task-dialog-category">
                {[
                  {v:"COMMERCIAL",l:"Commercial",c:"bg-emerald-100 text-emerald-700 border-emerald-300"},
                  {v:"CLIENT_DELIVERY",l:"Client Delivery",c:"bg-blue-100 text-blue-700 border-blue-300"},
                  {v:"ADMIN_FINANCE",l:"Admin / Finance",c:"bg-slate-100 text-slate-700 border-slate-300"},
                  {v:"INTERNAL",l:"Internal",c:"bg-purple-100 text-purple-700 border-purple-300"},
                ].map(cat=>(
                  <button
                    key={cat.v}
                    type="button"
                    data-testid={`mc-new-task-cat-${cat.v}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded border-2 transition ${newTaskForm.category===cat.v?cat.c:"bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                    onClick={()=>setNewTaskForm(p=>({...p,category:cat.v}))}
                  >{cat.l}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Client (optional)</Label>
              <Input value={newTaskForm.client_name} onChange={e=>{
                const val=e.target.value;
                const match=clientHealth.find(c=>c.company_name?.toLowerCase()===val.toLowerCase());
                setNewTaskForm(p=>({...p,client_name:val,client_id:match?.client_id||""}));
              }} placeholder="Type client name..." list="new-task-client-list"/>
              <datalist id="new-task-client-list">
                {clientHealth.map(c=><option key={c.client_id} value={c.company_name}/>)}
              </datalist>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Owner</Label>
                <Input value={newTaskForm.assigned_to_name} onChange={e=>setNewTaskForm(p=>({...p,assigned_to_name:e.target.value}))} placeholder="Assign to..."/></div>
              <div><Label className="text-xs">Due Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={newTaskForm.due_date} onChange={e=>setNewTaskForm(p=>({...p,due_date:e.target.value}))} data-testid="new-task-dialog-due"/></div>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={newTaskForm.priority} onValueChange={v=>setNewTaskForm(p=>({...p,priority:v}))}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={newTaskForm.description} onChange={e=>setNewTaskForm(p=>({...p,description:e.target.value}))} rows={2}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowNewTask(false)}>Cancel</Button>
            <Button disabled={!newTaskForm.title.trim()||!newTaskForm.category||!newTaskForm.due_date} data-testid="new-task-dialog-save" onClick={async()=>{
              const payload = {
                title: newTaskForm.title.trim(),
                description: newTaskForm.description,
                category: newTaskForm.category,
                client_id: newTaskForm.client_id,
                client_name: newTaskForm.client_name,
                link_type: newTaskForm.client_id ? "client" : "internal",
                assigned_to_name: newTaskForm.assigned_to_name,
                due_date: newTaskForm.due_date,
                priority: newTaskForm.priority,
                status: "Proposed",
                task_type: "general",
              };
              const r = await post("/scrum/v2/tasks", payload);
              if (r.ok) {
                toast.success("Task created");
                announceTaskChange();
                setShowNewTask(false);
                const execR = await f("/mission-control/execution-board");
                if (execR.ok) setExecBoard(await execR.json());
              } else {
                const e = await r.json().catch(()=>({}));
                toast.error(e.detail || "Failed");
              }
            }}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Task Dialog — soft-warn linking */}
      <Dialog open={!!showLinkTask} onOpenChange={()=>setShowLinkTask(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="link-task-dialog">
          <DialogHeader><DialogTitle>Link Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{showLinkTask?.title}</p>
            <div>
              <Label>Category</Label>
              <Select value={linkForm.category} onValueChange={v=>setLinkForm(p=>({...p,category:v}))}>
                <SelectTrigger><SelectValue placeholder="Pick a category..."/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMMERCIAL">Commercial</SelectItem>
                  <SelectItem value="CLIENT_DELIVERY">Client Delivery</SelectItem>
                  <SelectItem value="ADMIN_FINANCE">Admin / Finance</SelectItem>
                  <SelectItem value="INTERNAL">Internal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client (optional)</Label>
              <Input value={linkForm.client_name} onChange={e=>{
                const val=e.target.value;
                const match=clientHealth.find(c=>c.company_name?.toLowerCase()===val.toLowerCase());
                setLinkForm(p=>({...p,client_name:val,client_id:match?.client_id||""}));
              }} placeholder="Type client name..." list="link-client-list" data-testid="link-task-client-input"/>
              <datalist id="link-client-list">
                {clientHealth.map(c=><option key={c.client_id} value={c.company_name}/>)}
              </datalist>
              {linkForm.client_name && !linkForm.client_id && <p className="text-[10px] text-amber-600 mt-1">No matching client — will link by category only.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowLinkTask(null)}>Cancel</Button>
            <Button disabled={!linkForm.category && !linkForm.client_id} data-testid="save-link-btn" onClick={async()=>{
              const payload={};
              if(linkForm.category) payload.category=linkForm.category;
              if(linkForm.client_id) payload.client_id=linkForm.client_id;
              const r = await post(`/mission-control/tasks/${showLinkTask.task_id}/link`,payload);
              if(r.ok){
                toast.success("Task linked");
                // Refresh execution board
                const execR = await f("/mission-control/execution-board");
                if(execR.ok) setExecBoard(await execR.json());
                setShowLinkTask(null);
              } else {
                toast.error("Link failed");
              }
            }}>Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Next Best Action Dialog */}
      <Dialog open={!!showEditNBA} onOpenChange={()=>setShowEditNBA(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Next Best Action — {showEditNBA?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>What is the next best action?</Label>
            <Textarea value={nbaText} onChange={e=>setNbaText(e.target.value)} rows={3} placeholder="e.g., Schedule discovery call with CHRO this week" data-testid="edit-nba-textarea"/>
            <p className="text-xs text-slate-500">Saving locks this action until you update it manually.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowEditNBA(null)}>Cancel</Button>
            <Button disabled={!nbaText.trim()} data-testid="save-nba-btn" onClick={async()=>{
              const r = await post(`/mission-control/client-health/${showEditNBA.client_id}/next-action`,{next_best_action:nbaText.trim(),lock:true});
              if(r.ok){
                toast.success("Next best action updated");
                setClientHealth(p=>p.map(c=>c.client_id===showEditNBA.client_id?{...c,next_best_action:nbaText.trim(),next_best_action_locked:true}:c));
                setShowEditNBA(null);
              } else {
                toast.error("Failed to save");
              }
            }}>Save & Lock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REX-004B — Assign Analyst dialog. Surfaces only when more than one
          analyst is configured AND the task was just spawned without an
          explicit assignee. Single-analyst case is auto-assigned silently. */}
      <Dialog open={!!assignPrompt} onOpenChange={(o) => { if (!o) setAssignPrompt(null); }}>
        <DialogContent className="max-w-md" data-testid="assign-analyst-dialog">
          <DialogHeader>
            <DialogTitle>Assign Analyst</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              <span className="font-medium">Task:</span> {assignPrompt?.title}
            </p>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Send to</Label>
              <Select value={assignChoice} onValueChange={setAssignChoice}>
                <SelectTrigger data-testid="assign-analyst-select"><SelectValue placeholder="Pick an analyst" /></SelectTrigger>
                <SelectContent>
                  {(assignPrompt?.analysts || []).map((a) => (
                    <SelectItem key={a.email} value={a.email} data-testid={`assign-analyst-option-${a.email}`}>
                      {a.name || a.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignPrompt(null)} data-testid="assign-analyst-cancel">Skip</Button>
            <Button onClick={confirmAssign} disabled={!assignChoice} data-testid="assign-analyst-confirm">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REX-BRIEF-EVERYWHERE (May 2026) — universal "route with brief"
          modal. Any flow that wants to spawn a Lens task / content card
          / commit for a teammate sets `routeIntent` and the same modal
          handles the assignee + brief moment. See `routeToLensSmart()`
          for the chokepoint that funnels news triage, Top Commercial
          Moves, At-Risk client buttons and Replenishment "Route now"
          through here. */}
      {/* H1 — "Give to…" hand-off modal. Reuses the Lens assign control, but with a
          REAL-STAFF-ONLY pool (seed/test accounts excluded) and the giver's note as the brief. */}
      <AssignWithBriefModal
        open={!!giveIntent}
        onClose={() => setGiveIntent(null)}
        title="Assign to…"
        subtitle={giveIntent?.subtitle || ""}
        assignablePool={staffPool}
        currentUserEmail={user?.email}
        suggestedBrief=""
        confirmLabel="Assign to person"
        onConfirm={async (vals) => {
          const ok = await doHandoff(vals);
          return ok !== false;
        }}
      />

      {/* Share a news link with a colleague — one dialog, one email. NOT a hand-off:
          no task, no queue, no acceptance. Reuses the same modal shell with send-copy. */}
      <AssignWithBriefModal
        open={!!shareIntent}
        onClose={() => setShareIntent(null)}
        title="Pass this to a colleague"
        subtitle={shareIntent?.headline || ""}
        assignablePool={staffPool}
        currentUserEmail={user?.email}
        pickerLabel="Send to"
        pickerHint="An internal colleague — they'll get the link by email. No task, no to-do."
        briefLabel="Add a line of context"
        briefHint="Optional — a sentence on why it's worth their time."
        confirmLabel="Send link"
        onConfirm={async (vals) => {
          const ok = await doShare(vals);
          return ok !== false;
        }}
      />

      <AssignWithBriefModal
        open={!!routeIntent}
        onClose={() => {
          // Cancel callback first (so caller's Promise resolves null),
          // then clear the intent state.
          if (routeIntent?.onCancel) routeIntent.onCancel();
          setRouteIntent(null);
        }}
        title={routeIntent?.title || "Route this work"}
        subtitle={routeIntent?.subtitle || ""}
        assignablePool={assignablePool}
        currentUserEmail={user?.email}
        suggestedBrief={routeIntent?.suggestedBrief || ""}
        confirmLabel={routeIntent?.confirmLabel || "Route"}
        onConfirm={async (vals) => {
          if (!routeIntent?.onConfirm) return true;
          const ok = await routeIntent.onConfirm(vals);
          return ok !== false;
        }}
      />

      {/* REX-BRIEF (May 2026) — pop the assign+brief modal for an Upcoming Occasion.
          The actual POST to /api/content-calendar/markers/{id}/create-card carries
          assignee_email + brief_for_assignee so the assignee opens a card that
          already knows the angle. */}
      <AssignWithBriefModal
        open={!!occasionToAssign}
        onClose={() => setOccasionToAssign(null)}
        title="Create content for this occasion"
        subtitle={occasionToAssign?.title || ""}
        assignablePool={assignablePool}
        currentUserEmail={user?.email}
        suggestedBrief={buildOccasionBrief(occasionToAssign)}
        confirmLabel="Create &amp; assign"
        onConfirm={async ({ assignee_email, brief_for_assignee }) => {
          if (!occasionToAssign) return false;
          const id = occasionToAssign.marker_id;
          try {
            const r = await fetch(`${API}/content-calendar/markers/${id}/create-card`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ assignee_email, brief_for_assignee }),
            });
            if (!r.ok) {
              const e = await r.json().catch(() => ({}));
              toast.error(e.detail || "Couldn't create card");
              return false;
            }
            const d = await r.json();
            setOccasions(s => ({
              ...s,
              items: s.items.map(x => x.marker_id === id
                ? { ...x, linked_card_id: d.card_id, card_status: d.card_status || "Backlog" }
                : x),
            }));
            toast.success(
              d.already_existed
                ? "Card already exists"
                : assignee_email
                  ? `Card created · assigned to ${assignee_email.split("@")[0]}`
                  : "Card created in Backlog"
            );
            return true;
          } catch {
            toast.error("Network error");
            return false;
          }
        }}
      />
    </div>
  );
}

/* ─── REUSABLE TASK CARD (for Ops + Scrum Board) ─── */
function TaskCard({ t, onAct, onEdit, onClose }) {
  return (
    <div className="border border-slate-200 rounded-lg px-4 py-2 flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2"><span className="text-xs font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{t.status}</span>{t.owner_name&&<span className="text-sm text-slate-500">{t.owner_name}</span>}{t.client_name&&<span className="text-sm text-blue-500">{t.client_name}</span>}{t.due_date&&<span className={`text-sm font-medium ${urgCol(Math.floor((Date.now()-new Date(t.due_date).getTime())/86400000))}`}>{fmtDate(t.due_date)}</span>}</div>
        <p className="text-base text-slate-900 mt-0.5">{t.title}</p>
        {t.description&&<p className="text-sm text-slate-500 mt-0.5">{t.description.substring(0,80)}</p>}
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <Button size="sm" variant="outline" onClick={onEdit}><Edit3 className="h-4 w-4 mr-1"/>Edit</Button>
        <Button size="sm" className="bg-green-600 text-white" onClick={onClose}>Close</Button>
        <Button size="sm" variant="outline" className="text-red-600" onClick={()=>onAct(`/scrum/v2/tasks/${t.task_id}`,"PUT",{status:"Blocked"},"Blocked")}>Block</Button>
      </div>
    </div>
  );
}

function NewsCard({n,onOpen,onCommit,onDismiss,onActionRoute,onGive,onShare,faded}){
  const createContent = async () => {
    await fetch(`${window.location.origin}/api/mission-control/flywheel`,{method:"POST",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify({strategic_purpose:"market_authority",content_type:"linkedin_post",brand_mode:"remi_only",context_title:n.headline,context_summary:n.importance||"",timing:"publish_now"})});
    onCommit(n.headline,"marketing");
  };
  const priorityColors = {"MUST_ACT":"bg-red-600 text-white","HIGH_FOCUS":"bg-amber-500 text-white","MONITOR":"bg-slate-300 text-slate-700"};
  return(<div className={`border rounded-lg px-4 py-3 ${faded?"bg-white border-slate-100 opacity-70":"bg-slate-50 border-slate-200"} ${n.priority_label==="MUST_ACT"?"border-l-4 border-l-red-500":n.priority_label==="HIGH_FOCUS"?"border-l-4 border-l-amber-400":""}`}><div className="flex items-center gap-2 mb-1">{n.priority_label&&<span className={`text-[10px] font-bold px-2 py-0.5 rounded ${priorityColors[n.priority_label]||"bg-slate-200 text-slate-600"}`}>{n.priority_label.replace("_"," ")}</span>}{n.account_alert&&<span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-700 animate-pulse">ACCOUNT ALERT</span>}<span className={`text-xs font-bold px-2 py-0.5 rounded ${n.item_type==="news"?"bg-indigo-100 text-indigo-700":n.item_type==="rex_intel"?"bg-amber-100 text-amber-700":"bg-rose-100 text-rose-700"}`}>{n.item_type==="news"?"Market News":n.item_type==="rex_intel"?"Rex Intel":"Brief"}</span>{n.relevance_score>0&&<span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${n.relevance_score>=80?"bg-green-100 text-green-700":n.relevance_score>=50?"bg-amber-100 text-amber-700":"bg-slate-100 text-slate-500"}`} title="Relevance score">{n.relevance_score}%</span>}<span className="text-sm text-slate-500">{fmtDate(n.analyzed_at)}</span><span className="text-sm text-slate-400">{(n.organisations||[]).join(", ")||n.company||""} | {n.country}</span></div><p className="text-base font-semibold text-slate-900 cursor-pointer hover:text-blue-700 leading-snug" onClick={()=>onOpen(n)}>{n.headline}</p>{n.importance&&<p className="text-sm text-slate-600 mt-1 leading-snug line-clamp-2">{n.importance}</p>}{n.action_prompt&&<p className="text-xs text-red-600 font-semibold mt-1">{n.action_prompt}</p>}<div className="flex gap-2 mt-2">{n.source_url&&<a href={n.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5"/>Read</a>}<button className="text-xs text-green-700 hover:underline font-medium" onClick={()=>onActionRoute(n)}>Accept for Action</button><button data-testid={`news-give-${n.news_id}`} className="text-xs text-indigo-700 hover:underline font-medium" onClick={()=>onGive && onGive(n)}>Assign to…</button>{n.source_url&&<button data-testid={`news-share-${n.news_id}`} className="text-xs text-teal-700 hover:underline font-medium" onClick={()=>onShare && onShare(n)}>Send link</button>}<button className="text-xs text-blue-700 hover:underline font-medium" onClick={createContent}>Content</button><button className="text-xs text-purple-700 hover:underline font-medium" onClick={()=>onCommit(n.headline,"founder")}>Leader</button><button className="text-xs text-slate-400 hover:text-red-500 hover:underline" onClick={()=>onDismiss(n.news_id)}>Dismiss</button></div></div>);}

function TenderCard({t,onAct,onCommit,onDismiss}){return(<div data-testid={`tender-card-${t.tender_id}`} className="border border-emerald-200 rounded-lg bg-emerald-50/50 px-4 py-3"><div className="flex items-center gap-2 mb-1"><span className="text-xs font-bold px-2 py-0.5 rounded bg-emerald-200 text-emerald-800">Tender</span><span className="text-sm text-slate-500">{t.source_label||t.issuing_organisation||""} | {t.country||""}</span></div><p className="text-base font-semibold text-slate-900">{t.title}</p>{t.description&&<p className="text-sm text-slate-600 mt-1 line-clamp-2">{t.description}</p>}<div className="flex gap-2 mt-2">{t.url&&<a data-testid={`tender-view-${t.tender_id}`} href={t.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5"/>View</a>}<button data-testid={`tender-to-mc-${t.tender_id}`} className="text-xs text-purple-700 font-medium hover:underline" onClick={()=>onAct("/tenders/push-to-mission-control","POST",{tender_id:t.tender_id},"→ MC")}>To MC</button><button data-testid={`tender-commit-${t.tender_id}`} className="text-xs text-green-700 font-medium hover:underline" onClick={()=>onCommit(`Tender: ${t.title.substring(0,60)}`,"commercial")}>Commit</button><button data-testid={`tender-dismiss-${t.tender_id}`} className="text-xs text-slate-400 hover:text-red-500 hover:underline" onClick={()=>onDismiss(t.tender_id)}>Dismiss</button></div></div>);}

function EventCard({e,onOpen,navigate,onPosture,onDismiss}){
  const score=(e.relevance&&e.relevance.score)||0;
  const fmt=d=>{try{return new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});}catch{return d||"";}};
  const rec=(e.recommended_posture&&(e.recommended_posture.recommended||e.recommended_posture.posture))||"";
  const loc=[e.city,e.country].filter(Boolean).join(", ")||e.scope||"";
  const POSTURE_LABEL={attend:"Attending",speak:"Speaking",sponsor:"Sponsoring",outreach_only:"Outreach only",monitor:"Monitoring",skip:"Skipping"};
  const planned=e.our_posture?POSTURE_LABEL[e.our_posture]||e.our_posture:null;
  const stop=ev=>ev.stopPropagation();
  return(<div data-testid={`event-card-${e.event_id}`} className="border border-blue-200 rounded-lg bg-blue-50/50 px-4 py-3">
    <div className="flex items-center gap-2 mb-1 cursor-pointer" onClick={()=>onOpen&&onOpen(e)}>
      <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-200 text-blue-800">Event</span>
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${score>=70?"bg-emerald-100 text-emerald-700":score>=40?"bg-amber-100 text-amber-700":"bg-slate-100 text-slate-600"}`} title="Relevance / fit score">Fit {score}</span>
      <span className="text-sm text-slate-500 truncate">{loc}</span>
      {planned&&<span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`event-planned-${e.event_id}`}>{planned}</span>}
    </div>
    <p className="text-base font-semibold text-slate-900 cursor-pointer" onClick={()=>onOpen&&onOpen(e)}>{e.name}</p>
    <p className="text-sm text-slate-600 mt-0.5">{fmt(e.date_start)}{e.date_end&&e.date_end!==e.date_start?` – ${fmt(e.date_end)}`:""}{rec?` · Suggested: ${rec}`:""}</p>
    <div className="flex flex-wrap items-center gap-2 mt-2">
      {(e.url||e.source_url)&&<a data-testid={`event-view-${e.event_id}`} href={e.url||e.source_url} target="_blank" rel="noopener noreferrer" onClick={stop} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5"/>View</a>}
      <span className="text-xs text-slate-400">Plan:</span>
      <button data-testid={`event-attend-${e.event_id}`} className="text-xs font-medium text-emerald-700 hover:underline" onClick={ev=>{stop(ev);onPosture&&onPosture(e.event_id,"attend");}}>Attend</button>
      <button data-testid={`event-speak-${e.event_id}`} className="text-xs font-medium text-violet-700 hover:underline" onClick={ev=>{stop(ev);onPosture&&onPosture(e.event_id,"speak");}}>Speak</button>
      <button data-testid={`event-sponsor-${e.event_id}`} className="text-xs font-medium text-amber-700 hover:underline" onClick={ev=>{stop(ev);onPosture&&onPosture(e.event_id,"sponsor");}}>Sponsor</button>
      <button data-testid={`event-dismiss-${e.event_id}`} className="text-xs text-slate-400 hover:text-red-500 hover:underline" onClick={ev=>{stop(ev);onDismiss&&onDismiss(e.event_id);}}>Dismiss</button>
      <button data-testid={`event-to-radar-${e.event_id}`} className="text-xs text-indigo-700 hover:underline ml-auto" onClick={ev=>{stop(ev);navigate("/event-radar");}}>Open in Event Radar</button>
    </div>
  </div>);
}
